/**
 * The application state layer — PadLab's web counterpart to `ProjectSession` on iOS.
 *
 * It owns exactly three things:
 *
 * 1. **The open project, as signals.** No screen keeps a `Project` of its own.
 * 2. **`mutate(label, fn)` — the single write path.** Undo/redo, coalescing, autosave and
 *    engine synchronisation are consequences of going through it. A screen that edited the
 *    model directly would get none of them, which is why `project` is exposed read-only.
 * 3. **The live engine.** `audio/` ships decoding, effect graphs, offline rendering, the
 *    transport clock and the recorder; what did not exist was the thing that holds an
 *    `AudioContext` open, keeps a mixer strip per pad and turns a finger into a voice.
 *    `PadLabEngine` is that, and nothing else in the app talks to Web Audio directly.
 *
 * The rule that shapes the engine: **a touch reaches it before anything else happens.**
 * `trigger()` is synchronous, allocates only the two nodes the platform requires, and never
 * decodes, renders or awaits. Everything expensive happens in `preparePad`, which runs when
 * a pad is assigned or edited — never when a finger lands.
 */

import { batch, computed, effect, signal, type ReadonlySignal } from '../core/signal';
import { createLogger, idPrefix, tag } from '../core/log';
import {
  clamp,
  clampSampleEdit,
  effectChainActiveNodes,
  loopFitRate,
  loopNeedsTempoFit,
  makeAutomationLane,
  makeBank,
  makePattern,
  makeProject,
  makeSampleAsset,
  makeTrack,
  newUUID,
  nowISODate,
  padIsEmpty,
  playbackModeStopsOnRelease,
  projectAllPads,
  projectAsset,
  projectAssetIsLoop,
  projectPad,
  projectPadPlaysLoop,
  sampleEditTotalPitchCents,
  sanitiseProject,
  trackLeadingStepIndex,
  trackStepAtGlobalPosition,
  userOwnedLicence,
  type AutomationTarget,
  type Bank,
  type EffectChain,
  type ISODate,
  type Pad,
  type PadState,
  type Pattern,
  type Project,
  type SampleAsset,
  type Scene,
  type SampleEdit,
  type Step,
  type UUID,
} from '../model/types';
import {
  decodeAudio,
  DecodedSampleCache,
  decodedSampleFromBuffer,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_SECONDS,
  probeMediaDuration,
  tooLongError,
} from '../audio/decoder';
import { buildEffectChain, chainSignature, type EffectChainGraph } from '../audio/effects';
import { Metronome, Transport, type ScheduleWindow } from '../audio/transport';
import { PadLabRecorder, type RecordedTake } from '../audio/recorder';
import {
  encodeWav,
  faderTaper,
  renderEdit,
  renderedToAudioBuffer,
  toRenderedAudio,
  trackRandom,
  type RenderedAudio,
  type SeededRandom,
} from '../audio/offline-render';
import { getProjectStore, type ProjectSnapshot, type ProjectStore, type StorageReport } from '../storage/project-store';
import { artworkKey, assetKey, mediaKeyForAsset } from '../storage/db';
import { getBlobStore, type BlobStore } from '../storage/blob-store';
import {
  discardTakeRow,
  openTake,
  parkUndecodableTake,
  recoverableTakes,
  type RecoverableTake,
} from '../storage/takes';
import type { WaveformPeaks } from '../ui/components/waveform';
import { playHaptic } from '../platform/haptics';

const log = createLogger('state');

export type { RecordedTake, StorageReport };

export interface StereoLevel {
  /** The louder of the two channels — what a single-bar meter shows. */
  peak: number;
  rms: number;
  leftPeak: number;
  leftRMS: number;
  rightPeak: number;
  rightRMS: number;
}

export const SILENT_LEVEL: StereoLevel = { peak: 0, rms: 0, leftPeak: 0, leftRMS: 0, rightPeak: 0, rightRMS: 0 };

export interface TriggerOptions {
  /** 0…1, on top of the pad's own volume. */
  velocity?: number;
  /** Extra semitones — the sequencer's per-step pitch. */
  pitchOffset?: number;
  /** Absolute context time. Omitted means "the next block", i.e. now. */
  when?: number;
  /** Skips choke and retrigger handling, for the second and later hits of a ratchet. */
  isRatchet?: boolean;
  /**
   * Programmed rather than played. Set on every hit the sequencer schedules.
   *
   * The one thing it changes is the choke rule: a pattern must not cut off a pad the
   * performer is holding down. Native draws the same line — a sequenced request never
   * interrupts a live voice — and without it a hi-hat pattern chopped a held pad in the same
   * group on every eighth note.
   */
  isSequenced?: boolean;
  /**
   * `event.timeStamp` of the gesture that caused this hit, in the `performance.now()` time
   * base. Present only for a live touch or key, and only then is a latency sample recorded:
   * a sequenced or quantised hit is early or late on purpose, and folding musical intent
   * into a latency figure is how an app ends up reporting a number that means nothing.
   */
  gestureTime?: number;
}

function decibelsToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * Time constant for every fader and pan write in the live graph.
 *
 * A raw `param.value = x` steps the parameter at the start of the next render quantum. A
 * mixer drag, or an automation lane read at every step, produces one of those writes every
 * few milliseconds, and a gain that jumps in staircases is the zipper noise `effects.ts`
 * builds its whole `ramp()` helper to avoid. The same rule has to hold here.
 */
const FADER_SMOOTHING = 0.015;

function rampParam(param: AudioParam, value: number, now: number): void {
  if (!Number.isFinite(value)) return;
  param.setTargetAtTime(value, now, FADER_SMOOTHING);
}

/** Peak and RMS for one output channel, from a byte time-domain probe. */
function readChannelLevel(analyser: AnalyserNode, probe: Uint8Array<ArrayBuffer>): { peak: number; rms: number } {
  analyser.getByteTimeDomainData(probe);
  let peak = 0;
  let sum = 0;
  for (let index = 0; index < probe.length; index++) {
    const value = (probe[index]! - 128) / 128;
    const magnitude = Math.abs(value);
    if (magnitude > peak) peak = magnitude;
    sum += value * value;
  }
  return { peak, rms: Math.sqrt(sum / probe.length) };
}

// MARK: - Live engine

interface PreparedPad {
  buffer: AudioBuffer;
  offset: number;
  duration: number;
  rate: number;
  detuneCents: number;
  loop: boolean;
  loopStart: number;
  loopEnd: number;
  fadeIn: number;
  fadeOut: number;
  gain: number;
  attack: number;
  /**
   * Identity of everything this was built from, scheduling parameters included. A match
   * means nothing has to be done at all.
   */
  key: string;
  /**
   * Identity of just the parts that decide the *buffer*. When this matches but `key` does
   * not, only gain, pitch, rate, fades or loop points moved, and those are properties of the
   * voice rather than of the audio — the existing buffer is kept and the numbers updated.
   */
  renderKey: string;
  isRendered: boolean;
}

interface Voice {
  padID: UUID;
  source: AudioBufferSourceNode;
  env: GainNode;
  startedAt: number;
  endsAt: number;
  loop: boolean;
  /** A stop has been asked for. It may not have begun yet — see `releaseAt`. */
  releasing: boolean;
  /** Context time the release fade starts. `Infinity` until one is scheduled. */
  releaseAt: number;
  /** Plateau gain the attack ramps to; what a *scheduled* fade has to ramp down from. */
  level: number;
}

interface PadStrip {
  gain: GainNode;
  panner: StereoPannerNode | null;
  analyser: AnalyserNode;
  probe: Uint8Array<ArrayBuffer>;
  chain: EffectChainGraph | null;
  chainKey: string;
  /** False until the first `applyPad`, which sets the fader outright instead of ramping. */
  primed: boolean;
}

/** Everything about a pad's edit that changes how it must be rendered. */
/**
 * Identity of everything `preparePad` reads, not just everything it renders.
 *
 * `editRenderKey` alone was used as the "is this pad up to date" test, and it deliberately
 * excludes gain, pitch, rate, fades and loop points because none of them changes the
 * rendered buffer. But they *are* copied into `PreparedPad` and read on every trigger, so a
 * pad whose render key had not moved kept the gain, the pitch and the loop flag it was first
 * prepared with: every live control in the sample editor's Sound section wrote the document
 * and reached no sound. The pad's playback mode joins them, since it now decides whether the
 * voice loops.
 */
function padPrepareKey(pad: Pad, asset: SampleAsset): string {
  const edit = pad.edit;
  return [
    editRenderKey(pad, asset),
    pad.playbackMode,
    edit.gainDB,
    edit.pitchSemitones,
    edit.pitchCents,
    edit.playbackRate,
    edit.fadeInDuration,
    edit.fadeOutDuration,
    edit.loopEnabled ? 1 : 0,
    edit.loopStart ?? -1,
    edit.loopEnd ?? -1,
  ].join('|');
}

function editRenderKey(pad: Pad, asset: SampleAsset): string {
  const edit = pad.edit;
  return [
    asset.id,
    asset.contentHash,
    edit.startTime,
    edit.endTime ?? -1,
    edit.isReversed ? 1 : 0,
    edit.isNormalised ? edit.normalisationTargetDB : 'n',
    edit.silencedRegions.map((region) => `${region.lowerBound}:${region.upperBound}`).join(','),
    edit.preservesPitchWhenStretched ? edit.playbackRate : 1,
    edit.envelope === undefined ? 'e' : `${edit.envelope.attack}/${edit.envelope.decay}/${edit.envelope.sustain}`,
    chainSignature(edit.chain),
  ].join('~');
}

/**
 * True when the edit cannot be reproduced by scheduling parameters alone, so a buffer has to
 * be rendered up front. Fades, gain and loop points are excluded on purpose: they are exact
 * as envelope automation on the voice, and rendering them would cost a copy for nothing.
 */
function editNeedsRender(edit: SampleEdit): boolean {
  if (edit.isReversed || edit.isNormalised) return true;
  if (edit.silencedRegions.length > 0) return true;
  if (edit.envelope !== undefined) return true;
  if (edit.preservesPitchWhenStretched && edit.playbackRate !== 1) return true;
  return effectChainActiveNodes(edit.chain).length > 0;
}

export class PadLabEngine {
  readonly isUnlocked = signal(false);
  readonly sampleRate = signal(0);
  /** Milliseconds the browser reports for output. -1 when it declines to say. */
  readonly outputLatencyMs = signal(-1);
  /** Bumped whenever a voice starts or ends, so views can re-read activity cheaply. */
  readonly activity = signal(0);
  readonly meterTick = signal(0);

  private ctx: AudioContext | null = null;
  private preMaster: GainNode | null = null;
  private headroom: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private masterChain: EffectChainGraph | null = null;
  private masterChainKey = '';
  private masterGain: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  /**
   * One analyser per output channel. A stereo meter drawn from a single mono probe can never
   * show the two things it exists to show — an imbalance, or a dead channel — so the master
   * bus is split and read twice.
   */
  private leftAnalyser: AnalyserNode | null = null;
  private rightAnalyser: AnalyserNode | null = null;
  private leftProbe = new Uint8Array(new ArrayBuffer(2048));
  private rightProbe = new Uint8Array(new ArrayBuffer(2048));
  private previewBus: GainNode | null = null;

  private transportInstance: Transport | null = null;
  private metronomeInstance: Metronome | null = null;

  readonly samples = new DecodedSampleCache();
  private readonly buffers = new Map<UUID, AudioBuffer>();
  private readonly prepared = new Map<UUID, PreparedPad>();
  private readonly strips = new Map<UUID, PadStrip>();
  private readonly voices = new Map<UUID, Voice[]>();
  private readonly resumeOffsets = new Map<UUID, number>();
  /**
   * Pads a finger (or a key, or an assistive activation) is still on.
   *
   * Only modes that stop on release can be "held" in a way that matters — every other mode
   * is over the moment it is triggered — so membership is added by a live trigger on such a
   * pad and removed by the release that ends it.
   */
  private readonly held = new Set<UUID>();
  private readonly chokeGroups = new Map<number, Set<UUID>>();
  private readonly levels = new Map<UUID, number>();
  private readonly latencies: number[] = [];

  private masterLevels: StereoLevel = { ...SILENT_LEVEL };
  /** As `PadStrip.primed`, for the master bus. */
  private masterPrimed = false;
  private activityQueued = false;
  private routeWatchInstalled = false;
  private interrupted = false;
  /**
   * Counted, not assumed.
   *
   * Nothing anywhere in either build counted a dropout, a failed voice start or a stalled
   * render — the brief asks for exactly that and the answer was silence. A trigger that
   * cannot start a voice is counted here; a render clock that falls behind wall clock by
   * more than a buffer is counted as a glitch by `pumpMeters`.
   */
  readonly failedStarts = signal(0);
  readonly glitches = signal(0);
  /** Bumped whenever the graph was rebuilt around a new context or sample rate. */
  readonly routeChanged = signal(0);
  private lastClockCheck = { context: 0, wall: 0 };
  private previewVoice: { source: AudioBufferSourceNode; startedAt: number; endsAt: number } | null = null;
  private meterHolds = 0;

  context(): AudioContext | null {
    return this.ctx;
  }

  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /**
   * Creating the context is allowed without a gesture and leaves it suspended, which is what
   * lets decoding and graph construction happen during launch rather than on first touch.
   */
  ensureContext(): AudioContext {
    if (this.ctx !== null) return this.ctx;
    const Ctor: typeof AudioContext =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (globalThis as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.sampleRate.value = ctx.sampleRate;

    const master = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.2;
    const limiter = ctx.createDynamicsCompressor();
    limiter.ratio.value = 20;
    limiter.knee.value = 0;
    limiter.attack.value = 0.0008;
    limiter.release.value = 0.08;
    limiter.threshold.value = -0.5;
    const headroom = ctx.createGain();
    headroom.gain.value = decibelsToGain(-3);
    const pre = ctx.createGain();

    pre.connect(headroom).connect(limiter).connect(master).connect(analyser).connect(ctx.destination);
    this.preMaster = pre;
    this.headroom = headroom;
    this.limiter = limiter;
    this.masterGain = master;
    this.analyser = analyser;

    // `speakers` up-mixing on the way into the splitter, so a mono master still lights both
    // channels rather than reading as a dead right side.
    const stereo = ctx.createGain();
    stereo.channelCount = 2;
    stereo.channelCountMode = 'explicit';
    stereo.channelInterpretation = 'speakers';
    const splitter = ctx.createChannelSplitter(2);
    const left = ctx.createAnalyser();
    const right = ctx.createAnalyser();
    for (const side of [left, right]) {
      side.fftSize = 2048;
      side.smoothingTimeConstant = 0.2;
    }
    master.connect(stereo);
    stereo.connect(splitter);
    splitter.connect(left, 0);
    splitter.connect(right, 1);
    this.leftAnalyser = left;
    this.rightAnalyser = right;
    this.leftProbe = new Uint8Array(new ArrayBuffer(left.fftSize));
    this.rightProbe = new Uint8Array(new ArrayBuffer(right.fftSize));

    const preview = ctx.createGain();
    preview.connect(pre);
    this.previewBus = preview;
    const aux = ctx.createGain();
    aux.connect(pre);

    this.transportInstance = new Transport(ctx);
    this.metronomeInstance = new Metronome(ctx, aux);

    ctx.addEventListener('statechange', () => {
      this.isUnlocked.value = ctx.state === 'running';
      this.readLatency();
      // WebKit reports `interrupted` for a call, Siri or an alarm. It is not a member of the
      // standard `AudioContextState` union, which is why it has to be read as a string.
      const state = ctx.state as string;
      if (state === 'interrupted' || state === 'suspended') this.noteInterruption(state);
      else if (state === 'running') this.recoverFromInterruption();
    });
    this.watchRouteChanges();
    this.readLatency();
    return ctx;
  }

  /**
   * Route and sample-rate changes.
   *
   * `sampleRate` was read once at construction and never again, and there was no
   * `devicechange` listener anywhere in the app: plugging in headphones or an interface that
   * runs at a different rate left every decoded buffer resampled against a rate the context
   * no longer had, and nothing noticed. A rate change cannot be applied to a live
   * `AudioContext`, so the honest response is to rebuild the graph around a new one.
   */
  private watchRouteChanges(): void {
    if (this.routeWatchInstalled) return;
    this.routeWatchInstalled = true;
    if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) return;
    navigator.mediaDevices.addEventListener?.('devicechange', () => {
      const ctx = this.ctx;
      if (ctx === null) return;
      const rate = ctx.sampleRate;
      if (rate === this.sampleRate.peek()) return;
      log.info('Output route changed the sample rate', { from: this.sampleRate.peek(), to: rate });
      this.sampleRate.value = rate;
      this.rebuildAfterRouteChange();
    });
  }

  private noteInterruption(state: string): void {
    if (this.interrupted) return;
    this.interrupted = true;
    log.warn('Audio interrupted', { state: tag(state) });
    // Every voice is scheduled against a clock that has stopped. Letting them resume would
    // play a chord of everything that was in flight when the call arrived.
    for (const padID of [...this.voices.keys()]) this.stopPad(padID);
    this.held.clear();
    this.activity.value = this.activity.peek() + 1;
  }

  private recoverFromInterruption(): void {
    if (!this.interrupted) return;
    this.interrupted = false;
    const ctx = this.ctx;
    if (ctx === null) return;
    if (ctx.sampleRate !== this.sampleRate.peek()) {
      this.sampleRate.value = ctx.sampleRate;
      this.rebuildAfterRouteChange();
      return;
    }
    // The graph survived; only the strips' smoothed parameters may be mid-ramp.
    log.info('Audio resumed after an interruption');
  }

  /**
   * Drops every derived node and every decoded buffer so the next `applyPad` rebuilds them
   * against the context as it is now. Buffers are re-decoded rather than reused: an
   * `AudioBuffer` belongs to the rate it was decoded at.
   */
  private rebuildAfterRouteChange(): void {
    for (const padID of [...this.strips.keys()]) this.forget(padID);
    this.buffers.clear();
    this.samples.clear();
    this.prepared.clear();
    this.masterChain?.dispose();
    this.masterChain = null;
    this.masterChainKey = '';
    this.activity.value = this.activity.peek() + 1;
    this.routeChanged.value = this.routeChanged.peek() + 1;
  }

  get transport(): Transport {
    this.ensureContext();
    return this.transportInstance!;
  }

  get metronome(): Metronome {
    this.ensureContext();
    return this.metronomeInstance!;
  }

  /** The bus previews and monitoring share, so neither is affected by the master chain. */
  get previewDestination(): AudioNode {
    this.ensureContext();
    return this.previewBus!;
  }

  private readLatency(): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const base = typeof ctx.baseLatency === 'number' ? ctx.baseLatency : 0;
    const reported = (ctx as { outputLatency?: number }).outputLatency;
    const output = typeof reported === 'number' ? reported : 0;
    const total = (base + output) * 1000;
    this.outputLatencyMs.value = total > 0 ? total : -1;
  }

  async unlock(): Promise<boolean> {
    const ctx = this.ensureContext();
    try {
      if (ctx.state !== 'running') await ctx.resume();
      // A one-frame buffer through the graph is what actually convinces WebKit the context
      // was started by a gesture; `resume()` alone can resolve while still suspended.
      const primer = ctx.createBufferSource();
      primer.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      primer.connect(ctx.destination);
      primer.start();
      this.isUnlocked.value = ctx.state === 'running';
      this.readLatency();
      return this.isUnlocked.peek();
    } catch (error) {
      log.error('Audio unlock refused', error);
      return false;
    }
  }

  /**
   * `retainSource` copies the bytes so the caller can still use them afterwards, because
   * `decodeAudioData` detaches its input. Import does not need that — it keeps the original
   * `Blob`, not the `ArrayBuffer` — and paying for it there meant a 512 MB file existed
   * three times over before the size check in `decodeAudio` had even run.
   */
  decode(bytes: ArrayBuffer, fileName: string, options: { retainSource?: boolean } = {}): Promise<AudioBuffer> {
    return decodeAudio(this.ensureContext(), bytes, fileName, { retainSource: options.retainSource ?? true });
  }

  buffer(assetID: UUID): AudioBuffer | null {
    return this.buffers.get(assetID) ?? null;
  }

  putBuffer(assetID: UUID, buffer: AudioBuffer): void {
    this.buffers.set(assetID, buffer);
    this.samples.put(decodedSampleFromBuffer(assetID, buffer, { retainBuffer: true }));
  }

  triggerLatencies(): readonly number[] {
    return this.latencies;
  }

  private strip(padID: UUID): PadStrip {
    const existing = this.strips.get(padID);
    if (existing !== undefined) return existing;
    const ctx = this.ensureContext();
    const gain = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.35;
    let panner: StereoPannerNode | null = null;
    if (typeof ctx.createStereoPanner === 'function') {
      panner = ctx.createStereoPanner();
      gain.connect(panner).connect(analyser);
    } else {
      gain.connect(analyser);
    }
    analyser.connect(this.preMaster ?? ctx.destination);
    const strip: PadStrip = {
      gain,
      panner,
      analyser,
      probe: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
      chain: null,
      chainKey: '',
      primed: false,
    };
    this.strips.set(padID, strip);
    return strip;
  }

  /** Decodes and, when the edit needs it, renders. Cheap and idempotent once warm. */
  async preparePad(pad: Pad, asset: SampleAsset | undefined, blob: Blob | null, tempo: number): Promise<boolean> {
    if (asset === undefined) {
      this.prepared.delete(pad.id);
      return false;
    }
    const renderKey = editRenderKey(pad, asset);
    const key = padPrepareKey(pad, asset);
    const existing = this.prepared.get(pad.id);
    if (existing?.key === key) return true;
    if (existing !== undefined && existing.renderKey === renderKey) {
      // The buffer is still right; only numbers the voice reads have moved. Rebuilding it
      // would decode and possibly re-render a sound the user is dragging a slider over.
      this.prepared.set(pad.id, { ...existing, ...this.scheduling(pad, existing), key });
      this.resumeOffsets.delete(pad.id);
      return true;
    }

    let decoded = this.buffers.get(asset.id) ?? null;
    if (decoded === null) {
      if (blob === null) return false;
      decoded = await this.decode(await blob.arrayBuffer(), asset.originalFileName);
      this.putBuffer(asset.id, decoded);
    }

    const edit = pad.edit;
    const end = Math.min(edit.endTime ?? decoded.duration, decoded.duration);
    const start = clamp(edit.startTime, 0, end);

    /**
     * Two independent things ask for a loop and either is enough.
     *
     * `edit.loopEnabled` is a property of the *sample* — "this file is a loop, and here are
     * its points" — set in the wave editor. `playbackMode === 'loop'` is a property of the
     * *pad* — "hold this down and it repeats until I stop it" — set from the pad menu, whose
     * own summary promises exactly that. Only the first was ever read, so choosing Loop from
     * the menu changed the tile's caption and nothing else: the voice started with
     * `source.loop === false` and ended at the end of the sample.
     */
    // One Shot means one shot. The pad's own mode decides, so a library loop whose sample edit
    // carries `loopEnabled` stops looping the moment the user picks One Shot. The sample flag
    // still applies to the sustaining modes, where holding a looping bed is the point.
    const loops =
      pad.playbackMode === 'loop' || (pad.playbackMode !== 'oneShot' && edit.loopEnabled);

    let prepared: PreparedPad;
    if (editNeedsRender(edit)) {
      const rendered: RenderedAudio = await renderEdit(decoded, edit, { tempo, sourceName: asset.name });
      const buffer = renderedToAudioBuffer(this.ensureContext(), rendered);
      prepared = {
        buffer,
        offset: 0,
        duration: buffer.duration,
        rate: edit.preservesPitchWhenStretched ? 1 : edit.playbackRate,
        // A rendered buffer already contains the chain's own pitch move; only the edit's own
        // semitone/cent offset is left for the voice.
        detuneCents: sampleEditTotalPitchCents(edit),
        loop: loops,
        loopStart: Math.max(0, (edit.loopStart ?? start) - start),
        loopEnd: Math.max(0, (edit.loopEnd ?? end) - start),
        fadeIn: edit.fadeInDuration,
        fadeOut: edit.fadeOutDuration,
        gain: decibelsToGain(edit.gainDB),
        attack: 0,
        key,
        renderKey,
        isRendered: true,
      };
    } else {
      prepared = {
        buffer: decoded,
        offset: start,
        duration: Math.max(0.01, end - start),
        rate: edit.playbackRate,
        detuneCents: sampleEditTotalPitchCents(edit),
        loop: loops,
        loopStart: edit.loopStart ?? start,
        loopEnd: edit.loopEnd ?? end,
        fadeIn: edit.fadeInDuration,
        fadeOut: edit.fadeOutDuration,
        gain: decibelsToGain(edit.gainDB),
        attack: 0,
        key,
        renderKey,
        isRendered: false,
      };
    }
    this.prepared.set(pad.id, prepared);
    // A new buffer means the old playhead points into audio that no longer exists.
    this.resumeOffsets.delete(pad.id);
    return true;
  }

  /**
   * The parts of a `PreparedPad` that describe how to *play* the buffer rather than what is
   * in it. Recomputed in place when an edit moves one of them without moving the audio.
   */
  private scheduling(pad: Pad, existing: PreparedPad): Pick<PreparedPad, 'rate' | 'detuneCents' | 'loop' | 'loopStart' | 'loopEnd' | 'fadeIn' | 'fadeOut' | 'gain'> {
    const edit = pad.edit;
    // One Shot means one shot. The pad's own mode decides, so a library loop whose sample edit
    // carries `loopEnabled` stops looping the moment the user picks One Shot. The sample flag
    // still applies to the sustaining modes, where holding a looping bed is the point.
    const loops =
      pad.playbackMode === 'loop' || (pad.playbackMode !== 'oneShot' && edit.loopEnabled);
    const bufferEnd = existing.offset + existing.duration;
    const loopStart = existing.isRendered
      ? Math.max(0, (edit.loopStart ?? existing.offset) - existing.offset)
      : (edit.loopStart ?? existing.offset);
    const loopEnd = existing.isRendered
      ? Math.max(0, (edit.loopEnd ?? bufferEnd) - existing.offset)
      : (edit.loopEnd ?? bufferEnd);
    return {
      rate: existing.isRendered && edit.preservesPitchWhenStretched ? 1 : edit.playbackRate,
      detuneCents: sampleEditTotalPitchCents(edit),
      loop: loops,
      loopStart,
      loopEnd,
      fadeIn: edit.fadeInDuration,
      fadeOut: edit.fadeOutDuration,
      gain: decibelsToGain(edit.gainDB),
    };
  }

  isPrepared(padID: UUID): boolean {
    return this.prepared.has(padID);
  }

  /**
   * Moves one effect parameter on whichever live chain owns it — a pad strip or the master
   * bus. Returns false when no built chain has that node, which is the honest answer while
   * the graph is still cold.
   */
  setEffectParameter(nodeID: UUID, key: string, value: number): boolean {
    for (const strip of this.strips.values()) {
      if (strip.chain?.setParameter(nodeID, key, value) === true) return true;
    }
    return this.masterChain?.setParameter(nodeID, key, value) === true;
  }

  forget(padID: UUID): void {
    this.stopPad(padID);
    this.prepared.delete(padID);
    const strip = this.strips.get(padID);
    if (strip !== undefined) {
      strip.chain?.dispose();
      strip.analyser.disconnect();
      strip.gain.disconnect();
      strip.panner?.disconnect();
      this.strips.delete(padID);
    }
  }

  trigger(pad: Pad, options: TriggerOptions = {}): boolean {
    const prepared = this.prepared.get(pad.id);
    const ctx = this.ctx;
    if (prepared === undefined || ctx === null) {
      // A pad that was asked to play and could not. Counting it is the difference between
      // "latency is low" and a report that can be believed.
      if (!padIsEmpty(pad)) this.failedStarts.value = this.failedStarts.peek() + 1;
      return false;
    }
    const callStart = performance.now();
    if (ctx.state !== 'running') void ctx.resume();

    const when = options.when ?? ctx.currentTime;
    // A quantised hit, and every sequencer hit, is handed to the engine up to a lookahead
    // window (150 ms) before it sounds. Choking and restarting *now* would open a silent
    // hole in front of the hit that justifies them, which at slow tempos is plainly audible.
    // Both are scheduled for `when` instead, so the outgoing voice ends as the new one starts.
    const stopAt = when > ctx.currentTime + 0.001 ? when : undefined;

    const existing = this.voices.get(pad.id) ?? [];
    if (options.isRatchet !== true) {
      if (pad.retrigger === 'ignore' && existing.some((voice) => !voice.releasing)) return true;
      if (pad.retrigger === 'restart') this.stopPad(pad.id, stopAt);
      if (pad.retrigger === 'layer' && existing.length >= pad.polyphony) {
        const oldest = existing[0];
        if (oldest !== undefined) this.stopVoice(oldest, 0.006, stopAt);
      }
      if (pad.chokeGroup !== undefined) {
        for (const other of this.chokeGroups.get(pad.chokeGroup) ?? []) {
          if (other === pad.id) continue;
          // A pattern never cuts a pad the performer is holding. `held` is the set of pads
          // whose playback mode stops on release and whose finger has not come up, so a
          // sequenced hi-hat stops chopping the held note in the same choke group on every
          // eighth — the same line the native engine draws between programmed and played.
          if (options.isSequenced === true && this.held.has(other)) continue;
          this.stopPad(other, stopAt);
        }
      }
    }

    const strip = this.strip(pad.id);
    const env = ctx.createGain();
    const source = ctx.createBufferSource();
    source.buffer = prepared.buffer;
    source.playbackRate.value = prepared.rate;
    const cents = prepared.detuneCents + (options.pitchOffset ?? 0) * 100;
    if (cents !== 0) {
      if (source.detune !== undefined) source.detune.value = cents;
      else source.playbackRate.value = prepared.rate * Math.pow(2, cents / 1200);
    }
    if (prepared.loop) {
      source.loop = true;
      source.loopStart = prepared.loopStart;
      source.loopEnd = prepared.loopEnd > prepared.loopStart ? prepared.loopEnd : prepared.buffer.duration;
    }

    const level = prepared.gain * clamp(options.velocity ?? 1, 0, 1);
    const attack = Math.max(prepared.fadeIn, prepared.attack, 0.0015);
    env.gain.setValueAtTime(0.0001, when);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), when + attack);

    const sounding = prepared.loop ? Number.POSITIVE_INFINITY : prepared.duration / Math.max(0.01, prepared.rate);
    if (!prepared.loop && prepared.fadeOut > 0) {
      const fadeStart = when + Math.max(0, sounding - prepared.fadeOut);
      env.gain.setValueAtTime(Math.max(0.0002, level), fadeStart);
      env.gain.exponentialRampToValueAtTime(0.0001, fadeStart + prepared.fadeOut);
    }

    source.connect(env).connect(strip.chain?.input ?? strip.gain);
    const offset =
      pad.retrigger === 'resume'
        ? clamp(this.resumeOffsets.get(pad.id) ?? prepared.offset, 0, prepared.buffer.duration)
        : prepared.offset;
    if (prepared.loop) source.start(when, offset);
    else source.start(when, offset, Math.max(0.01, prepared.duration - (offset - prepared.offset)));

    const voice: Voice = {
      padID: pad.id,
      source,
      env,
      startedAt: when,
      endsAt: prepared.loop ? Number.POSITIVE_INFINITY : when + sounding,
      loop: prepared.loop,
      releasing: false,
      releaseAt: Number.POSITIVE_INFINITY,
      level: Math.max(0.0002, level),
    };
    const list = this.voices.get(pad.id);
    if (list === undefined) this.voices.set(pad.id, [voice]);
    else list.push(voice);
    // A played hit on a mode that stops on release is a hold until `release` says otherwise.
    if (options.isSequenced !== true && playbackModeStopsOnRelease(pad.playbackMode)) {
      this.held.add(pad.id);
    }
    source.onended = (): void => this.dropVoice(voice);
    this.bumpActivity();

    // Touch to sound, not the length of this function.
    //
    // The old line measured `performance.now() - callStart`, which is how long `trigger`
    // took — a number that says nothing about what the user experienced and is small no
    // matter how badly the app is behaving. What matters is the whole path: the browser's
    // event delivery (`timeStamp` → handler entry), this call, the gap between scheduling
    // and the block that plays it, and the output device's own buffer.
    if (options.gestureTime !== undefined && options.when === undefined) {
      const delivery = Math.max(0, callStart - options.gestureTime);
      const work = performance.now() - callStart;
      const scheduling = Math.max(0, (when - ctx.currentTime) * 1000);
      this.latencies.push(delivery + work + scheduling + Math.max(0, this.outputLatencyMs.peek()));
      if (this.latencies.length > 400) this.latencies.shift();
    }
    return true;
  }

  private dropVoice(voice: Voice): void {
    const list = this.voices.get(voice.padID);
    if (list !== undefined) {
      const index = list.indexOf(voice);
      if (index >= 0) list.splice(index, 1);
      if (list.length === 0) this.voices.delete(voice.padID);
    }
    // A hold-mode sample that simply ran out is no longer being held, whatever the finger is
    // doing; leaving the id in the set would exempt the pad from sequenced chokes for ever.
    if (this.voices.get(voice.padID) === undefined) this.held.delete(voice.padID);
    // Ran to the end rather than being stopped: the playhead is back at the top, and leaving
    // a stale offset behind would make the *next* hit start from wherever the last release
    // happened to land.
    if (!voice.releasing) this.resumeOffsets.delete(voice.padID);
    try {
      voice.env.disconnect();
    } catch {
      // Already torn down by a panic; nothing left to undo.
    }
    this.bumpActivity();
  }

  /**
   * Announces that the set of live voices changed — on the next frame, never inside the
   * trigger.
   *
   * `activity` is a signal, and this graph flushes synchronously, so writing it from
   * `trigger()` ran the board's per-tile DOM pass inside the `pointerdown` handler that had
   * just scheduled a sample. Every view that reads `activity` also reads `meterTick`, which
   * ticks once per animation frame, so nothing is any less live for waiting for the frame —
   * and the audio path no longer touches the DOM at all.
   */
  private bumpActivity(): void {
    if (this.activityQueued) return;
    this.activityQueued = true;
    const flush = (): void => {
      this.activityQueued = false;
      this.activity.value = this.activity.peek() + 1;
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush);
    else setTimeout(flush, 0);
  }

  /**
   * `at` schedules the fade for a moment in the future rather than starting it now. The
   * sequencer uses it so a choke lands with the hit that justifies it.
   */
  private stopVoice(voice: Voice, fade: number, at?: number): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    voice.releasing = true;
    const now = ctx.currentTime;
    const start = at === undefined ? now : Math.max(now, at);
    voice.releaseAt = start;
    const seconds = Math.max(0.004, fade);
    const gain = voice.env.gain;
    try {
      if (at === undefined) {
        gain.cancelScheduledValues(start);
        gain.setValueAtTime(Math.max(0.0002, gain.value), start);
      } else if (typeof gain.cancelAndHoldAtTime === 'function') {
        // Holds whatever the envelope has actually reached at `start`. Reading `.value` is
        // no use for a fade that begins in the future, and plain `cancelScheduledValues`
        // would drop the attack's endpoint and leave the parameter at its starting floor.
        gain.cancelAndHoldAtTime(start);
      } else {
        gain.cancelScheduledValues(start);
        gain.setValueAtTime(voice.level, start);
      }
      gain.exponentialRampToValueAtTime(0.0001, start + seconds);
      voice.source.stop(start + seconds + 0.005);
    } catch {
      this.dropVoice(voice);
    }
    this.rememberPlayhead(voice, start);
  }

  /**
   * Where in the *buffer* a voice had reached at `at`.
   *
   * The old line stored `start - voice.startedAt`, which is elapsed wall-clock time from
   * zero: it ignored where the sample was trimmed to start and ignored that a rate-shifted
   * voice covers more or less buffer than clock. On a trimmed or pitched pad "Continue from
   * the current playhead" therefore resumed somewhere else entirely. A looping voice has no
   * single playhead worth resuming from, so it does not get one.
   */
  private rememberPlayhead(voice: Voice, at: number): void {
    if (voice.loop) return;
    const prepared = this.prepared.get(voice.padID);
    if (prepared === undefined) return;
    const elapsed = Math.max(0, at - voice.startedAt);
    const rate = Math.max(0.01, prepared.rate) * Math.pow(2, prepared.detuneCents / 1200);
    const position = prepared.offset + elapsed * rate;
    const end = prepared.offset + prepared.duration;
    // A voice stopped within a hair of the end has nothing left to continue into; the next
    // hit starts at the top rather than playing four milliseconds of tail.
    if (position >= end - 0.01) this.resumeOffsets.delete(voice.padID);
    else this.resumeOffsets.set(voice.padID, clamp(position, prepared.offset, end));
  }

  release(padID: UUID, releaseFade: number, at?: number): void {
    this.held.delete(padID);
    for (const voice of [...(this.voices.get(padID) ?? [])]) this.stopVoice(voice, releaseFade, at);
  }

  stopPad(padID: UUID, at?: number): void {
    this.held.delete(padID);
    // `stopVoice` records where each voice had reached; deleting the entry here threw that
    // away one line after writing it, which left `resume` working only for a pad released
    // mid-sample and never for a toggle, a loop or a restart-retrigger.
    for (const voice of [...(this.voices.get(padID) ?? [])]) this.stopVoice(voice, 0.008, at);
  }

  /** Forgets a pad's stored playhead, so the next hit starts at the beginning. */
  rewind(padID: UUID): void {
    this.resumeOffsets.delete(padID);
  }

  /**
   * Silences every sounding pad and the preview, and leaves each pad's stored playhead alone.
   *
   * Split out of `panic()` so that opening a menu can go quiet without also being destructive.
   * A `resume` pad silenced this way continues where it was, which is what someone opening a
   * menu over a running loop expects; `panic()` is the one that forgets.
   */
  stopAllPads(): void {
    for (const padID of [...this.voices.keys()]) this.stopPad(padID);
    this.stopPreview();
    this.held.clear();
    // This must land now rather than on the next frame: the board has to go dark immediately.
    this.activity.value = this.activity.peek() + 1;
  }

  panic(): void {
    this.stopAllPads();
    this.resumeOffsets.clear();
  }

  /**
   * A voice whose release is scheduled for later is still sounding until that moment — the
   * tile must not go dark a lookahead window before the choke it is waiting for.
   */
  private isLive(voice: Voice): boolean {
    const now = this.now();
    // Not yet. A quantised hit is pushed into `voices` the instant the finger lands, and a
    // sequenced one up to a lookahead window early, so treating membership as "sounding"
    // lit the tile as `playing` before a sample had been played — which is why the whole
    // `queued` state, its glyph, its tone and its accessibility string were unreachable.
    if (now + 0.001 < voice.startedAt) return false;
    return !voice.releasing || now < voice.releaseAt;
  }

  isSounding(padID: UUID): boolean {
    return (this.voices.get(padID) ?? []).some((voice) => this.isLive(voice));
  }

  isLooping(padID: UUID): boolean {
    return (this.voices.get(padID) ?? []).some((voice) => voice.loop && this.isLive(voice));
  }

  progress(padID: UUID): number {
    const list = this.voices.get(padID);
    const voice = list?.[list.length - 1];
    if (voice === undefined || this.ctx === null) return 0;
    if (!Number.isFinite(voice.endsAt)) {
      const prepared = this.prepared.get(padID);
      const span = prepared === undefined ? 1 : Math.max(0.05, prepared.loopEnd - prepared.loopStart);
      return ((this.ctx.currentTime - voice.startedAt) % span) / span;
    }
    const span = voice.endsAt - voice.startedAt;
    return span <= 0 ? 0 : clamp((this.ctx.currentTime - voice.startedAt) / span, 0, 1);
  }

  padLevel(padID: UUID): number {
    return this.levels.get(padID) ?? 0;
  }

  masterLevel(): StereoLevel {
    return this.masterLevels;
  }

  gainReductionDB(): number {
    const reduction = this.limiter?.reduction;
    return typeof reduction === 'number' ? reduction : 0;
  }

  applyPad(pad: Pad, anySoloed: boolean, tempo: number): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const strip = this.strip(pad.id);
    const audible = pad.isMuted || (anySoloed && !pad.isSoloed) ? 0 : 1;
    const now = ctx.currentTime;
    // `faderTaper`, not the raw position: the renderer cubes every fader
    // (`offline-render.ts:3068`) and so does the native engine, so a linear write here made
    // a pad at 0.5 play at -6 dB and export at -18 dB — a different mix, not a rounding
    // difference.
    // The first write lands outright. A brand-new GainNode sits at unity, and easing down to
    // the pad's real level over a time constant would make the first hit after a load audible
    // at up to +6 dB. Every write after that ramps.
    const level = faderTaper(pad.volume) * audible;
    const pan = clamp(pad.pan, -1, 1);
    if (strip.primed) {
      rampParam(strip.gain.gain, level, now);
      if (strip.panner !== null) rampParam(strip.panner.pan, pan, now);
    } else {
      strip.primed = true;
      strip.gain.gain.value = level;
      if (strip.panner !== null) strip.panner.pan.value = pan;
    }

    // The realtime chain is what you hear while performing; anything it cannot express was
    // already baked into the prepared buffer.
    const key = chainSignature(pad.edit.chain);
    if (key !== strip.chainKey) {
      strip.chain?.dispose();
      strip.chainKey = key;
      strip.chain = null;
      if (effectChainActiveNodes(pad.edit.chain).length > 0 && !editNeedsRender(pad.edit)) {
        const graph = buildEffectChain(ctx, pad.edit.chain, { tempo });
        graph.output.connect(strip.gain);
        strip.chain = graph;
      }
    } else {
      strip.chain?.setTempo(tempo);
    }

    for (const [group, members] of [...this.chokeGroups]) {
      members.delete(pad.id);
      if (members.size === 0) this.chokeGroups.delete(group);
    }
    if (pad.chokeGroup !== undefined) {
      const set = this.chokeGroups.get(pad.chokeGroup) ?? new Set<UUID>();
      set.add(pad.id);
      this.chokeGroups.set(pad.chokeGroup, set);
    }
  }

  applyMaster(volume: number, headroomDB: number, limiterEnabled: boolean, ceilingDB: number, chain: EffectChain, tempo: number): void {
    const ctx = this.ctx;
    if (ctx === null || this.masterGain === null || this.headroom === null || this.limiter === null) return;
    const now = ctx.currentTime;
    // Cubed, to match `offline-render.ts:2976`. A master fader that means one thing live and
    // another on bounce makes every export a surprise.
    const master = faderTaper(clamp(volume, 0, 1));
    const trim = decibelsToGain(headroomDB);
    if (this.masterPrimed) {
      rampParam(this.masterGain.gain, master, now);
      rampParam(this.headroom.gain, trim, now);
    } else {
      this.masterPrimed = true;
      this.masterGain.gain.value = master;
      this.headroom.gain.value = trim;
    }
    this.limiter.threshold.value = limiterEnabled ? ceilingDB : 0;
    this.limiter.ratio.value = limiterEnabled ? 20 : 1;

    const key = chainSignature(chain);
    if (key === this.masterChainKey) {
      this.masterChain?.setTempo(tempo);
      return;
    }
    this.masterChainKey = key;
    const previous = this.masterChain;
    this.masterChain = null;
    const pre = this.preMaster!;
    pre.disconnect();
    previous?.dispose();
    if (effectChainActiveNodes(chain).length > 0) {
      const graph = buildEffectChain(ctx, chain, { tempo });
      pre.connect(graph.input);
      graph.output.connect(this.headroom);
      this.masterChain = graph;
    } else {
      pre.connect(this.headroom);
    }
  }

  previewBuffer(buffer: AudioBuffer, options: { offset?: number; duration?: number; rate?: number; detune?: number } = {}): void {
    const ctx = this.ensureContext();
    if (ctx.state !== 'running') void ctx.resume();
    this.stopPreview();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = options.rate ?? 1;
    if (options.detune !== undefined && source.detune !== undefined) source.detune.value = options.detune;
    source.connect(this.previewBus ?? ctx.destination);
    const offset = clamp(options.offset ?? 0, 0, buffer.duration);
    const duration = Math.max(0.01, options.duration ?? buffer.duration - offset);
    source.start(ctx.currentTime, offset, duration);
    const voice = { source, startedAt: ctx.currentTime, endsAt: ctx.currentTime + duration / (options.rate ?? 1) };
    this.previewVoice = voice;
    source.onended = (): void => {
      if (this.previewVoice === voice) this.previewVoice = null;
      this.activity.value = this.activity.peek() + 1;
    };
    this.activity.value = this.activity.peek() + 1;
  }

  stopPreview(): void {
    const voice = this.previewVoice;
    if (voice === null) return;
    this.previewVoice = null;
    try {
      voice.source.stop();
    } catch {
      // Already finished on its own.
    }
  }

  previewProgress(): number {
    const voice = this.previewVoice;
    if (voice === null || this.ctx === null) return 0;
    const span = voice.endsAt - voice.startedAt;
    return span <= 0 ? 0 : clamp((this.ctx.currentTime - voice.startedAt) / span, 0, 1);
  }

  isPreviewing(): boolean {
    return this.previewVoice !== null;
  }

  /** Frequency magnitudes for the Sound Lab spectrum, normalised to 0…1. */
  /**
   * The master bus as a waveform, -1…1, for a live scope.
   *
   * The Sound Lab showed a spectrum and nothing else; the brief asks for animated waveform
   * *and* spectrum feedback, and one of the two is not both.
   */
  readScope(into: Float32Array): void {
    const analyser = this.analyser;
    if (analyser === null) {
      into.fill(0);
      return;
    }
    const frames = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(frames);
    const per = Math.max(1, Math.floor(frames.length / into.length));
    for (let index = 0; index < into.length; index++) {
      // Peak rather than mean: a scope that averages a waveform draws a flat line.
      let peak = 0;
      for (let step = 0; step < per; step++) {
        const value = frames[index * per + step] ?? 0;
        if (Math.abs(value) > Math.abs(peak)) peak = value;
      }
      into[index] = peak;
    }
  }

  readSpectrum(into: Float32Array): void {
    const analyser = this.analyser;
    if (analyser === null) {
      into.fill(0);
      return;
    }
    const bins = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
    analyser.getByteFrequencyData(bins);
    const perBand = Math.max(1, Math.floor(bins.length / into.length));
    for (let band = 0; band < into.length; band++) {
      let sum = 0;
      for (let index = 0; index < perBand; index++) sum += bins[band * perBand + index] ?? 0;
      into[band] = sum / perBand / 255;
    }
  }

  acquireMeters(): () => void {
    this.meterHolds++;
    if (this.meterHolds === 1) this.runMeterLoop();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.meterHolds = Math.max(0, this.meterHolds - 1);
    };
  }

  private runMeterLoop(): void {
    const step = (): void => {
      if (this.meterHolds <= 0) return;
      const left = this.leftAnalyser;
      const right = this.rightAnalyser;
      if (left !== null && right !== null) {
        const l = readChannelLevel(left, this.leftProbe);
        const r = readChannelLevel(right, this.rightProbe);
        this.masterLevels = {
          peak: Math.max(l.peak, r.peak),
          rms: Math.sqrt((l.rms * l.rms + r.rms * r.rms) / 2),
          leftPeak: l.peak,
          leftRMS: l.rms,
          rightPeak: r.peak,
          rightRMS: r.rms,
        };
      }
      for (const [padID, strip] of this.strips) {
        strip.analyser.getByteTimeDomainData(strip.probe);
        let peak = 0;
        for (let index = 0; index < strip.probe.length; index += 2) {
          const magnitude = Math.abs((strip.probe[index]! - 128) / 128);
          if (magnitude > peak) peak = magnitude;
        }
        this.levels.set(padID, peak);
      }
      this.detectGlitch();
      this.meterTick.value = this.meterTick.peek() + 1;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /**
   * A dropout, measured rather than claimed.
   *
   * `AudioContext.currentTime` advances only while the render thread is producing blocks. If
   * wall clock moves further than the audio clock did, the audio thread lost that time —
   * which is precisely an underrun. A generous threshold (60 ms, several render quanta) keeps
   * ordinary scheduler jitter and backgrounded tabs out of the count.
   */
  private detectGlitch(): void {
    const ctx = this.ctx;
    if (ctx === null || ctx.state !== 'running') {
      this.lastClockCheck = { context: 0, wall: 0 };
      return;
    }
    const context = ctx.currentTime;
    const wall = performance.now() / 1000;
    const previous = this.lastClockCheck;
    this.lastClockCheck = { context, wall };
    if (previous.wall === 0) return;
    const wallDelta = wall - previous.wall;
    // A long gap means the page was not being animated at all; that is not a dropout.
    if (wallDelta <= 0 || wallDelta > 0.5) return;
    const shortfall = wallDelta - (context - previous.context);
    if (shortfall > 0.06) this.glitches.value = this.glitches.peek() + 1;
  }
}

export const engine = new PadLabEngine();

export const recorder = new PadLabRecorder({
  host: {
    context: () => engine.context(),
    now: () => engine.now(),
    unlock: () => engine.unlock(),
    click: (accented, when, volume) => engine.metronome.schedule(when, accented, volume),
  },
  get monitorDestination(): AudioNode {
    return engine.previewDestination;
  },
  // Takes go to disk as they are captured. Without a project there is nothing to file them
  // under, so the vault declines rather than inventing an owner.
  vault: {
    open: async (meta) => {
      const open = projectSignal.peek();
      const blobs = blobsSignal.peek();
      if (open === null || blobs === null) return null;
      return openTake(blobs, open.id, meta);
    },
    park: async (blob, meta) => {
      const open = projectSignal.peek();
      const blobs = blobsSignal.peek();
      if (open === null || blobs === null) return;
      await parkUndecodableTake(blobs, open.id, blob, meta);
    },
  },
});

/**
 * Takes this device is holding that the user has not yet saved — an interrupted recording,
 * or one finished in a session that ended before it was filed anywhere.
 */
export async function pendingRecoveredTakes(): Promise<RecoverableTake[]> {
  const open = projectSignal.peek();
  if (open === null) return [];
  return recoverableTakes(open.id);
}

/** Forgets a recovered take, and the audio behind it. */
export async function dropRecoveredTake(id: UUID): Promise<void> {
  await discardTakeRow(id, blobsSignal.peek() ?? undefined);
}

/** Reads a recovered take's bytes back so it can be imported or exported. */
export async function recoveredTakeBlob(take: RecoverableTake): Promise<Blob | null> {
  const blobs = blobsSignal.peek();
  if (blobs === null) return null;
  return (await blobs.tryGet(take.blobKey)) ?? null;
}

// MARK: - Analysis worker
//
// One module worker for the whole app. Peaks, onset detection and tempo estimation all run
// here so a 40 MB import never costs the main thread a frame.

type AnalysisReply =
  | { kind: 'progress'; id: number; fraction: number }
  | { kind: 'peaks'; id: number; minima: Float32Array; maxima: Float32Array; secondsPerBucket: number; duration: number }
  | { kind: 'transients'; id: number; times: Float64Array; strengths: Float32Array }
  | { kind: 'tempo'; id: number; bpm: number | null; confidence: number }
  | { kind: 'hash'; id: number; hex: string | null }
  | { kind: 'cancelled'; id: number }
  | { kind: 'error'; id: number; message: string; recovery: string };

/**
 * Analysis runs in a worker, and a worker that fails to load must not be able to hang the
 * app.
 *
 * `new Worker()` does not throw when the script cannot be fetched — it reports the failure
 * asynchronously on `onerror`. This class previously listened only for `onmessage`, so a
 * failed load left every pending promise unsettled forever, and because the dead worker was
 * still cached in `this.worker`, every later call joined the same queue and hung too.
 *
 * That was reachable in the worst possible place: with a service worker in control from the
 * first byte — i.e. every launch after the first — WebKit can refuse to re-fetch a `worker`
 * destination in a cross-origin-isolated page, the request 504s, and the app opened once and
 * then never again.
 *
 * Three guards now: the error is heard, every pending request is settled when it fires, and
 * a request that never gets any answer at all times out. A dead worker is dropped so the
 * next call builds a fresh one instead of inheriting the corpse.
 */
class AnalysisClient {
  private worker: Worker | null = null;
  private nextID = 1;
  private readonly pending = new Map<
    number,
    { resolve: (value: AnalysisReply) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();

  /** Generous: hashing a long import is legitimately slow on an old phone. */
  private static readonly REQUEST_TIMEOUT_MS = 30_000;

  private settle(id: number, apply: (entry: { resolve: (value: AnalysisReply) => void; reject: (error: Error) => void }) => void): void {
    const entry = this.pending.get(id);
    if (entry === undefined) return;
    clearTimeout(entry.timer);
    this.pending.delete(id);
    apply(entry);
  }

  /** Settles every outstanding request and discards the worker so the next call retries. */
  private failAll(reason: string): void {
    const error = new Error(reason);
    for (const [id] of [...this.pending]) {
      this.settle(id, (entry) => entry.reject(error));
    }
    if (this.worker !== null) {
      this.worker.onerror = null;
      this.worker.onmessage = null;
      this.worker.terminate();
      this.worker = null;
    }
  }

  private ensure(): Worker | null {
    if (this.worker !== null) return this.worker;
    if (typeof Worker === 'undefined') return null;
    try {
      const worker = new Worker(new URL('../workers/analysis.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (event: MessageEvent<AnalysisReply>): void => {
        const reply = event.data;
        if (reply.kind === 'progress') return;
        this.settle(reply.id, (entry) => {
          if (reply.kind === 'error') entry.reject(new Error(reply.message));
          else entry.resolve(reply);
        });
      };
      // The load failure path. Without this the app hangs instead of degrading.
      worker.onerror = (): void => {
        this.failAll('Background audio analysis could not start, so waveforms and tempo detection are unavailable.');
      };
      // A structured-clone failure is rarer but just as fatal to a waiting promise.
      worker.onmessageerror = (): void => {
        this.failAll('Background audio analysis sent a message this browser could not read.');
      };
      this.worker = worker;
      return worker;
    } catch (error) {
      log.error('Analysis worker unavailable', error);
      return null;
    }
  }

  private send(message: Record<string, unknown>, transfer: Transferable[]): Promise<AnalysisReply> {
    const worker = this.ensure();
    if (worker === null) return Promise.reject(new Error('This browser cannot run background audio analysis.'));
    const id = this.nextID++;
    return new Promise<AnalysisReply>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.settle(id, (entry) =>
          entry.reject(new Error('Background audio analysis stopped responding.')),
        );
        // A worker that answered nothing is not trustworthy for the next request either.
        if (this.pending.size === 0) this.failAll('Background audio analysis stopped responding.');
      }, AnalysisClient.REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        worker.postMessage({ ...message, id }, transfer);
      } catch (error) {
        this.settle(id, (entry) => entry.reject(error instanceof Error ? error : new Error(String(error))));
      }
    });
  }

  async peaks(buffer: AudioBuffer, peaksPerSecond: number): Promise<WaveformPeaks> {
    const channels: ArrayBuffer[] = [];
    for (let index = 0; index < Math.min(2, buffer.numberOfChannels); index++) {
      channels.push(new Float32Array(buffer.getChannelData(index)).buffer);
    }
    const reply = await this.send({ kind: 'peaks', channels, sampleRate: buffer.sampleRate, peaksPerSecond }, channels);
    if (reply.kind !== 'peaks') throw new Error('Waveform analysis returned nothing usable.');
    return { minima: reply.minima, maxima: reply.maxima, secondsPerBucket: reply.secondsPerBucket, duration: reply.duration };
  }

  async transients(buffer: AudioBuffer, sensitivity: number): Promise<number[]> {
    const samples = new Float32Array(buffer.getChannelData(0)).buffer;
    const reply = await this.send({ kind: 'transients', samples, sampleRate: buffer.sampleRate, sensitivity }, [samples]);
    return reply.kind === 'transients' ? [...reply.times] : [];
  }

  async tempo(buffer: AudioBuffer): Promise<{ bpm: number | null; confidence: number }> {
    const samples = new Float32Array(buffer.getChannelData(0)).buffer;
    const reply = await this.send({ kind: 'tempo', samples, sampleRate: buffer.sampleRate }, [samples]);
    return reply.kind === 'tempo' ? { bpm: reply.bpm, confidence: reply.confidence } : { bpm: null, confidence: 0 };
  }

  async hash(blob: Blob): Promise<string> {
    const stream = this.nextID;
    const bytes = await blob.arrayBuffer();
    const reply = await this.send({ kind: 'hash', stream, chunk: bytes, final: true }, [bytes]);
    return reply.kind === 'hash' && reply.hex !== null ? reply.hex : '';
  }
}

export const analysis = new AnalysisClient();

// MARK: - Session

const UNDO_LIMIT = 80;
const COALESCE_WINDOW_MS = 900;
export const DELETED_RETENTION_DAYS = 30;

interface UndoEntry {
  label: string;
  coalesceKey: string | null;
  snapshot: Project;
  at: number;
}

export interface MutateOptions {
  /** Consecutive mutations sharing a key inside the coalesce window become one undo step. */
  coalesceKey?: string;
  /** Skips the engine push, for edits that cannot change how anything sounds. */
  silent?: boolean;
  /**
   * Skips the undo snapshot, for writes the user did not make and cannot meaningfully
   * reverse — background library generation is the only one. Without it, twenty-five
   * "Add Library Sounds" entries clone a growing document and push the user's real edits
   * off the end of an eighty-entry stack, so a first launch ends with an undo history that
   * contains nothing the user did.
   */
  history?: false;
}

/** The steps PadLab's own text-size control offers, as a multiplier on the type scale. */
export const TEXT_SCALES: readonly number[] = [0.9, 1, 1.15, 1.3, 1.5];

export interface AppSettings {
  /**
   * Multiplier on the whole type scale.
   *
   * Safari on iOS applies the OS text size to a page automatically now that
   * `text-size-adjust` is no longer pinned, but a desktop browser and an installed window do
   * not, and the brief asks for the size to be adjustable in the app. Both routes work
   * together: this multiplies whatever the platform already decided.
   */
  textScale: number;
  hapticsEnabled: boolean;
  monitoringDefault: boolean;
  wakeLockEnabled: boolean;
  advancedMode: boolean;
  countInEnabled: boolean;
  onboardingComplete: boolean;
  installGuideSeen: boolean;
  micEducationSeen: boolean;
  /**
   * Openings of the step inspector that still teach the drag gesture. Counts down on open and
   * is zeroed the first time a value is actually dragged, because a user who has done it does
   * not need telling again.
   */
  stepDragHintsLeft: number;
  starterContentVersion: number;
  /**
   * Catalogue version the generated sound library was last built from. Zero means it has
   * never been built, which is what makes a first run start generating and a later launch
   * that already has it do nothing.
   */
  libraryContentVersion: number;
}

const DEFAULT_SETTINGS: AppSettings = {
  hapticsEnabled: true,
  monitoringDefault: false,
  wakeLockEnabled: true,
  advancedMode: false,
  countInEnabled: true,
  textScale: 1,
  onboardingComplete: false,
  installGuideSeen: false,
  micEducationSeen: false,
  stepDragHintsLeft: 3,
  starterContentVersion: 0,
  libraryContentVersion: 0,
};

const SETTINGS_KEY = 'padlab.settings';

const projectSignal = signal<Project | null>(null);
const revisionSignal = signal(0);
const undoStack: UndoEntry[] = [];
const redoStack: UndoEntry[] = [];
const undoLabelSignal = signal<string | null>(null);
const redoLabelSignal = signal<string | null>(null);
const settingsSignal = signal<AppSettings>({ ...DEFAULT_SETTINGS });
const storageReportSignal = signal<StorageReport | null>(null);

export const project: ReadonlySignal<Project | null> = projectSignal;
export const revision: ReadonlySignal<number> = revisionSignal;
export const undoLabel: ReadonlySignal<string | null> = undoLabelSignal;
export const redoLabel: ReadonlySignal<string | null> = redoLabelSignal;
export const settings: ReadonlySignal<AppSettings> = settingsSignal;
export const storageReport: ReadonlySignal<StorageReport | null> = storageReportSignal;
/**
 * Identity of the open document. Changes when a different project is opened and *not* when
 * one is edited or restored by undo, which is what lets a screen rebuild on "open" without
 * being torn down and rebuilt on every edit.
 */
export const openProjectID = computed(() => projectSignal.value?.id ?? null);

export const canUndo = computed(() => undoLabelSignal.value !== null);
export const canRedo = computed(() => redoLabelSignal.value !== null);

/** UI selection that is not part of the saved document. */
export const currentBankIndex = signal(0);
export const selectedPadID = signal<UUID | null>(null);
export const selectedAssetID = signal<UUID | null>(null);
export const currentPatternID = signal<UUID | null>(null);
export const boardEditMode = signal(false);
export const padClipboard = signal<Pad | null>(null);
export const stepClipboard = signal<readonly Step[] | null>(null);

/**
 * The open stores, held in signals rather than plain `let`s.
 *
 * This is not stylistic. A `computed` records the signals it read; a body that returns
 * early on `store === null` reads *nothing*, so its dependency set comes out empty and it
 * can never recompute — it stays frozen at `[]` for the life of the page. That is exactly
 * what happened: when a reload landed on a project route, `projects` was first evaluated
 * before `bootstrap()` had assigned the store, and the Home screen then showed
 * "No projects yet" forever while the database held three of them. Reading the signal
 * first means the dependency is always recorded, whether or not the store exists yet.
 */
const storeSignal = signal<ProjectStore | null>(null);
const blobsSignal = signal<BlobStore | null>(null);

export function projectStore(): ProjectStore {
  const value = storeSignal.peek();
  if (value === null) throw new Error('PadLab storage is still opening.');
  return value;
}

export function blobStore(): BlobStore {
  const value = blobsSignal.peek();
  if (value === null) throw new Error('PadLab storage is still opening.');
  return value;
}

export const projects = computed(() => storeSignal.value?.summaries.value ?? []);
export const trashedProjects = computed(() => storeSignal.value?.trashed.value ?? []);
export const autosaveStatus = computed(
  () => storeSignal.value?.autosaveStatus.value ?? { kind: 'idle' as const },
);

/**
 * Reads a value out of the open project and re-derives it whenever anything changes. The
 * only sanctioned way for a screen to look at project data.
 */
export function derive<T>(read: (open: Project) => T, fallback: T): ReadonlySignal<T> {
  return computed(() => {
    revisionSignal.value;
    const open = projectSignal.value;
    return open === null ? fallback : read(open);
  });
}

export const currentBank = derive(
  (open) => open.banks[Math.min(currentBankIndex.value, open.banks.length - 1)] ?? open.banks[0] ?? null,
  null as Bank | null,
);

export const currentPattern = derive((open) => {
  const id = currentPatternID.value;
  return open.patterns.find((entry) => entry.id === id) ?? open.patterns[0] ?? null;
}, null as Pattern | null);

let syncQueued = false;

function queueSync(): void {
  if (syncQueued) return;
  syncQueued = true;
  queueMicrotask(() => {
    syncQueued = false;
    syncEngine();
  });
}

function syncEngine(): void {
  const open = projectSignal.peek();
  if (open === null || engine.context() === null) return;
  const anySoloed = projectAllPads(open).some((pad) => pad.isSoloed);
  for (const bank of open.banks) {
    for (const pad of bank.pads) {
      if (padIsEmpty(pad)) continue;
      engine.applyPad(pad, anySoloed, open.tempo);
    }
  }
  engine.applyMaster(
    open.master.volume,
    open.master.headroomDB,
    open.master.limiterEnabled,
    open.master.limiterCeilingDB,
    open.master.chain,
    open.tempo,
  );
  engine.transport.setTempo(open.tempo);
  engine.transport.setSwing(open.swing);
  void preloadPads(open);
}

const preparedKeys = new Map<UUID, string>();

/**
 * Why a pad is not playable, when it is not.
 *
 * `preloadPads` used to catch its failures, log them and tell nobody, so a pad whose file
 * was gone or whose bytes would not decode fell through `padStateFor` to `processing` — the
 * hourglass and the word "Working" — for ever. The model has had `missingSource` and
 * `decodeFailed` states, glyphs and accessibility strings all along; this is what makes them
 * reachable.
 */
type PadIssue = 'missingSource' | 'decodeFailed';
const padIssues = new Map<UUID, PadIssue>();
const reportedIssues = new Set<UUID>();

export function padIssue(padID: UUID): PadIssue | undefined {
  return padIssues.get(padID);
}

function notePadIssue(pad: Pad, issue: PadIssue): void {
  padIssues.set(pad.id, issue);
  if (reportedIssues.has(pad.id)) return;
  reportedIssues.add(pad.id);
  const label = pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name;
  pushBanner({
    kind: 'warning',
    title: issue === 'missingSource' ? `${label} has lost its sound file` : `${label} could not be decoded`,
    message:
      issue === 'missingSource'
        ? 'The audio behind it is not on this device any more. Assign another sound, or import the original again.'
        : 'The file is on this device but this browser will not decode it. Convert it to WAV or M4A and import it again.',
    timeout: 8000,
  });
}

function clearPadIssue(padID: UUID): void {
  padIssues.delete(padID);
  reportedIssues.delete(padID);
}

/** Decode and, where the edit needs it, render — always ahead of the first touch. */
export async function preloadPads(open: Project): Promise<void> {
  for (const bank of open.banks) {
    for (const pad of bank.pads) {
      const asset = projectAsset(open, pad.assetID);
      if (asset === undefined) {
        if (preparedKeys.delete(pad.id)) engine.forget(pad.id);
        clearPadIssue(pad.id);
        continue;
      }
      const key = padPrepareKey(pad, asset);
      if (preparedKeys.get(pad.id) === key) continue;
      preparedKeys.set(pad.id, key);
      try {
        const blob = engine.buffer(asset.id) === null ? await blobStore().tryGet(mediaKeyForAsset(open.id, asset)) : null;
        const ready = await engine.preparePad(pad, asset, blob ?? null, open.tempo);
        if (ready) clearPadIssue(pad.id);
        else {
          // `preparePad` returns false only when it has neither a decoded buffer nor bytes
          // to decode: the file this pad points at is gone from the device.
          preparedKeys.delete(pad.id);
          notePadIssue(pad, 'missingSource');
        }
      } catch (error) {
        preparedKeys.delete(pad.id);
        notePadIssue(pad, 'decodeFailed');
        log.error('Pad preparation failed', error, { pad: idPrefix(pad.id) });
      }
    }
  }
}

function refreshUndoLabels(): void {
  undoLabelSignal.value = undoStack.length > 0 ? undoStack[undoStack.length - 1]!.label : null;
  redoLabelSignal.value = redoStack.length > 0 ? redoStack[redoStack.length - 1]!.label : null;
}

/**
 * The one write path. Everything a screen changes about the document goes through here, so
 * undo, autosave and engine synchronisation happen without any screen arranging them.
 */
export function mutate(label: string, change: (open: Project) => void, options: MutateOptions = {}): void {
  const open = projectSignal.peek();
  if (open === null) return;
  const now = performance.now();
  const top = undoStack[undoStack.length - 1];
  const coalesceKey = options.coalesceKey ?? null;
  const coalescing = coalesceKey !== null && top !== undefined && top.coalesceKey === coalesceKey && now - top.at < COALESCE_WINDOW_MS;

  if (options.history === false) {
    // Nothing pushed and nothing cleared: a background write must not consume the user's
    // history, and it must not discard a redo they are still entitled to.
  } else if (coalescing && top !== undefined) {
    top.at = now;
    top.label = label;
  } else {
    undoStack.push({ label, coalesceKey, snapshot: structuredClone(open), at: now });
    if (undoStack.length > UNDO_LIMIT) undoStack.shift();
    redoStack.length = 0;
  }

  batch(() => {
    change(open);
    open.modifiedAt = nowISODate();
    revisionSignal.value = revisionSignal.peek() + 1;
    refreshUndoLabels();
  });
  storeSignal.peek()?.markDirty(open);
  if (options.silent !== true) queueSync();
}

function restore(entry: UndoEntry, into: 'undo' | 'redo'): void {
  const open = projectSignal.peek();
  if (open === null) return;
  const mirror: UndoEntry = { label: entry.label, coalesceKey: entry.coalesceKey, snapshot: structuredClone(open), at: performance.now() };
  if (into === 'redo') redoStack.push(mirror);
  else undoStack.push(mirror);
  const restored = sanitiseProject(entry.snapshot);
  batch(() => {
    projectSignal.value = restored;
    revisionSignal.value = revisionSignal.peek() + 1;
    refreshUndoLabels();
  });
  storeSignal.peek()?.markDirty(restored);
  queueSync();
}

export function undo(): void {
  const entry = undoStack.pop();
  if (entry !== undefined) restore(entry, 'redo');
}

export function redo(): void {
  const entry = redoStack.pop();
  if (entry !== undefined) restore(entry, 'undo');
}

export function clearHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
  refreshUndoLabels();
}

export async function saveNow(): Promise<void> {
  await storeSignal.peek()?.flush();
}

/**
 * Version history.
 *
 * `snapshots`, `takeSnapshot` and `restoreSnapshot` were complete in the store and had no
 * caller anywhere in the app: snapshots were written, kept, and reachable only as an
 * internal fallback when a document failed to parse. These are the three functions a screen
 * needs to put them in front of the user.
 */
export async function projectVersions(): Promise<ProjectSnapshot[]> {
  const open = projectSignal.peek();
  if (open === null) return [];
  return projectStore().snapshots(open.id);
}

/** An explicit, labelled version — the "save a version I can come back to" action. */
export async function saveProjectVersion(label: string): Promise<void> {
  const open = projectSignal.peek();
  if (open === null) return;
  await projectStore().takeSnapshot(open, label);
}

/**
 * Restores a version and reopens it. The version being left is kept, so restoring is itself
 * undoable by restoring the other way.
 */
export async function restoreProjectVersion(snapshotID: string): Promise<void> {
  const open = projectSignal.peek();
  if (open === null) return;
  const restored = await projectStore().restoreSnapshot(snapshotID, open.id);
  batch(() => {
    projectSignal.value = restored;
    revisionSignal.value = revisionSignal.peek() + 1;
  });
  clearHistory();
  queueSync();
}

// MARK: - Banners

export type BannerKind = 'info' | 'success' | 'warning' | 'danger';

export interface AppBanner {
  id: string;
  kind: BannerKind;
  title: string;
  message: string;
  actionLabel?: string;
  action?: () => void;
  /** Milliseconds; 0 keeps the banner until it is dismissed. */
  timeout: number;
}

const bannersSignal = signal<readonly AppBanner[]>([]);
export const banners: ReadonlySignal<readonly AppBanner[]> = bannersSignal;
let bannerSeed = 0;

export function pushBanner(banner: Omit<AppBanner, 'id'>): string {
  const id = `banner-${++bannerSeed}`;
  const entry: AppBanner = { ...banner, id };
  bannersSignal.value = [...bannersSignal.peek().filter((existing) => existing.title !== entry.title), entry];
  if (entry.timeout > 0) setTimeout(() => dismissBanner(id), entry.timeout);
  return id;
}

export function dismissBanner(id: string): void {
  bannersSignal.value = bannersSignal.peek().filter((entry) => entry.id !== id);
}

export function notify(title: string, message = ''): void {
  pushBanner({ kind: 'success', title, message, timeout: 2600 });
}

/**
 * A notice that carries the undo it promises.
 *
 * Four dialogs in the app tell the user "undo brings it back" — clearing a pad, removing a
 * sound, wiping a pattern, randomising a rack. The stack behind that promise is real and 80
 * entries deep, but on a phone there is no keystroke to reach it, so the offer has to travel
 * with the notice that makes it. `⌘Z` remains the route for a hardware keyboard.
 */
export function notifyUndoable(title: string, message = ''): void {
  if (undoLabelSignal.peek() === null) {
    notify(title, message);
    return;
  }
  pushBanner({
    kind: 'info',
    title,
    message,
    actionLabel: 'Undo',
    action: () => undo(),
    // Long enough to read the notice and decide, short enough not to become furniture.
    timeout: 9000,
  });
}

export function reportFailure(title: string, error: unknown): void {
  const message = error instanceof Error ? error.message : 'PadLab could not complete that.';
  const recovery = (error as { recoverySuggestion?: string } | null)?.recoverySuggestion;
  pushBanner({ kind: 'danger', title, message: recovery === undefined ? message : `${message} ${recovery}`, timeout: 7000 });
}

// MARK: - Settings

export function updateSettings(change: Partial<AppSettings>): void {
  const next = { ...settingsSignal.peek(), ...change };
  settingsSignal.value = next;
  applyTextScale(next.textScale);
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  } catch (error) {
    // Private-mode Safari refuses to write. The app still works for this session.
    log.warn('Settings could not be persisted', { reason: tag(error instanceof Error ? error.name : 'unknown') });
  }
}

/** Publishes the chosen multiplier to CSS. One custom property drives the whole scale. */
export function applyTextScale(scale: number): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--type-scale', String(clamp(scale, 0.8, 1.6)));
}

function loadSettings(): void {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw !== null) settingsSignal.value = { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AppSettings>) };
  } catch {
    settingsSignal.value = { ...DEFAULT_SETTINGS };
  }
  applyTextScale(settingsSignal.peek().textScale);
}

/**
 * Physical feedback for a pad hit. A buzz where the Vibration API exists, a light tap on iOS
 * via the one mechanism Safari offers — see `platform/haptics` for what that is and why.
 */
export function haptic(pattern: number | number[] = 8): void {
  if (!settingsSignal.peek().hapticsEnabled) return;
  playHaptic(pattern);
}

// MARK: - Project lifecycle

export async function openProject(id: UUID): Promise<boolean> {
  try {
    const outcome = await projectStore().loadWithOutcome(id);
    batch(() => {
      projectSignal.value = outcome.project;
      currentBankIndex.value = 0;
      currentPatternID.value = outcome.project.patterns[0]?.id ?? null;
      selectedPadID.value = null;
      revisionSignal.value = revisionSignal.peek() + 1;
    });
    clearHistory();
    preparedKeys.clear();
    await projectStore().setLastOpenedProjectID(id);
    queueSync();
    if (outcome.recovery !== undefined) {
      pushBanner({ kind: 'warning', title: 'Recovered from a backup', message: outcome.recovery, timeout: 0 });
    }
    return true;
  } catch (error) {
    reportFailure('That project could not be opened', error);
    return false;
  }
}

export async function adoptProject(next: Project): Promise<void> {
  await projectStore().save(next);
  batch(() => {
    projectSignal.value = next;
    currentBankIndex.value = 0;
    currentPatternID.value = next.patterns[0]?.id ?? null;
    selectedPadID.value = null;
    revisionSignal.value = revisionSignal.peek() + 1;
  });
  clearHistory();
  preparedKeys.clear();
  await projectStore().setLastOpenedProjectID(next.id);
  queueSync();
}

export async function refreshStorageReport(): Promise<void> {
  if (storeSignal.peek() === null) return;
  try {
    storageReportSignal.value = await projectStore().storageReport();
  } catch (error) {
    log.error('Storage report unavailable', error);
  }
}

export function daysUntilPurge(deletedAt: ISODate): number {
  const elapsed = Date.now() - new Date(deletedAt).getTime();
  return Math.max(0, DELETED_RETENTION_DAYS - Math.floor(elapsed / (24 * 60 * 60 * 1000)));
}

// MARK: - Assets

export interface ImportResult {
  asset: SampleAsset;
  buffer: AudioBuffer;
}

export { MAX_IMPORT_BYTES };

export async function importAudioBlob(
  blob: Blob,
  displayName: string,
  origin: SampleAsset['origin'],
  options: { caption?: string; tags?: string[] } = {},
): Promise<ImportResult> {
  if (blob.size === 0) throw new Error('That file is empty.');
  // Everything cheap first, in the order that costs least.
  //
  // The size limit lived inside `decodeAudio`, i.e. behind `blob.arrayBuffer()` and two
  // slices of it, so the file was read into memory three times *before* anything decided it
  // was too big. The duration probe is the check a byte count cannot make: an hour of speech
  // is a small MP3 and a 600 MB decoded buffer.
  if (blob.size > MAX_IMPORT_BYTES) {
    throw new Error(
      `“${displayName}” is larger than the ${Math.round(MAX_IMPORT_BYTES / (1024 * 1024))} MB import limit. Trim it, or export a shorter section, and import it again.`,
    );
  }
  const probed = await probeMediaDuration(blob);
  if (probed !== null && probed > MAX_IMPORT_SECONDS) throw tooLongError(displayName, probed);

  const bytes = await blob.arrayBuffer();
  // The extension is a hint, never a promise: a mislabelled file is caught right here. No
  // `retainSource`: the encoded original is the `Blob`, which is untouched by decoding.
  const buffer = await engine.decode(bytes, displayName, { retainSource: false });
  const hash = await analysis.hash(blob).catch(() => '');

  const open = projectSignal.peek();
  if (open === null) throw new Error('Open a project before importing a sound.');
  const existing = hash === '' ? undefined : open.assets.find((asset) => asset.contentHash === hash);
  if (existing !== undefined) {
    engine.putBuffer(existing.id, buffer);
    return { asset: existing, buffer };
  }

  // The blob key is `<projectID>.<assetID>`, which is the convention the project store's
  // package export, duplicate and delete paths all walk. `fileName` is the portable name
  // *inside* a package, and must stay a plain file name so the archive is readable.
  const id = newUUID();
  await blobStore().put(assetKey(open.id, id), blob, { mimeType: blob.type });
  const asset = makeSampleAsset({
    id,
    name: displayName,
    fileName: `${id}.${blob.type.includes('wav') ? 'wav' : 'bin'}`,
    originalFileName: displayName,
    origin,
    duration: buffer.duration,
    sampleRate: buffer.sampleRate,
    channelCount: buffer.numberOfChannels,
    frameCount: buffer.length,
    byteSize: blob.size,
    contentHash: hash,
    licence: userOwnedLicence(),
    ...(options.caption === undefined ? {} : { caption: options.caption }),
    ...(options.tags === undefined ? {} : { tags: options.tags }),
  });
  engine.putBuffer(asset.id, buffer);
  mutate('Import Sound', (target) => {
    target.assets.push(asset);
  });
  return { asset, buffer };
}

// MARK: - Pad artwork
//
// `Pad.artworkFileName` has been in both schemas from the start and was never written, never
// read and never drawn. It holds the id of a picture in the blob store, under a key the
// clear-cache sweep never touches — artwork is something the user chose, not something the
// app can regenerate.

/** Largest picture worth keeping for a tile that is at most a couple of hundred points wide. */
const MAX_ARTWORK_BYTES = 4 * 1024 * 1024;

export async function setPadArtwork(padID: UUID, file: Blob): Promise<void> {
  const open = projectSignal.peek();
  if (open === null) return;
  if (!file.type.startsWith('image/')) throw new Error('Pad artwork has to be an image.');
  if (file.size > MAX_ARTWORK_BYTES) {
    throw new Error(`That image is larger than the ${Math.round(MAX_ARTWORK_BYTES / (1024 * 1024))} MB artwork limit.`);
  }
  const previous = projectPad(open, padID)?.artworkFileName;
  const id = newUUID();
  await blobStore().put(artworkKey(open.id, id), file, { mimeType: file.type });
  // `mutate` repaints every tile before it returns, and a tile repainting here asks for this
  // pad's picture. The cache it asks has to be keyed by the picture, not by the pad, or the
  // answer is the picture being replaced.
  mutate('Pad Artwork', (target) => {
    const live = projectPad(target, padID);
    if (live !== undefined) live.artworkFileName = id;
  });
  if (previous !== undefined) {
    await blobStore().delete(artworkKey(open.id, previous)).catch(() => undefined);
    // After the await, so the tile has been handed the new URL before the old one goes.
    releaseArtworkURL(open.id, previous);
  }
}

export async function clearPadArtwork(padID: UUID): Promise<void> {
  const open = projectSignal.peek();
  if (open === null) return;
  const previous = projectPad(open, padID)?.artworkFileName;
  mutate('Remove Pad Artwork', (target) => {
    const live = projectPad(target, padID);
    if (live !== undefined) delete live.artworkFileName;
  });
  if (previous !== undefined) {
    releaseArtworkURL(open.id, previous);
    await blobStore().delete(artworkKey(open.id, previous)).catch(() => undefined);
  }
}

/**
 * Object URLs handed to tiles, kept so they can be revoked rather than leaked.
 *
 * Keyed by the blob-store key, never by pad id. A pad id does not name a picture: replacing a
 * pad's artwork keeps the id while the picture changes, and a duplicated project keeps every
 * pad id, so two projects would have shared one entry.
 */
const artworkURLs = new Map<string, string>();

function releaseArtworkURL(projectID: UUID, artworkID: string | undefined): void {
  if (artworkID === undefined) return;
  const key = artworkKey(projectID, artworkID);
  const stale = artworkURLs.get(key);
  if (stale === undefined) return;
  URL.revokeObjectURL(stale);
  artworkURLs.delete(key);
}

export async function padArtworkURL(pad: Pad): Promise<string | null> {
  const open = projectSignal.peek();
  if (open === null || pad.artworkFileName === undefined) return null;
  const key = artworkKey(open.id, pad.artworkFileName);
  const cached = artworkURLs.get(key);
  if (cached !== undefined) return cached;
  const blob = await blobStore().tryGet(key);
  if (blob === undefined) return null;
  const url = URL.createObjectURL(blob);
  artworkURLs.set(key, url);
  return url;
}

export async function bufferForAsset(asset: SampleAsset): Promise<AudioBuffer | null> {
  const cached = engine.buffer(asset.id);
  if (cached !== null) return cached;
  const open = projectSignal.peek();
  if (open === null) return null;
  const blob = await blobStore().tryGet(mediaKeyForAsset(open.id, asset));
  if (blob === undefined) return null;
  try {
    const buffer = await engine.decode(await blob.arrayBuffer(), asset.originalFileName);
    engine.putBuffer(asset.id, buffer);
    return buffer;
  } catch (error) {
    log.error('Asset decode failed', error, { asset: idPrefix(asset.id) });
    return null;
  }
}

/**
 * Puts a sound on a pad, with the playback settings that sound actually wants.
 *
 * A loop is a bed: it is a bar or more of music, so it belongs *under* the grid rather than
 * inside one of its cells. Assigning one defaults the pad to looping playback quantised to
 * the bar, which is what makes hitting it lock to the grid instead of starting wherever the
 * finger landed. One-shots keep whatever the pad was set to.
 *
 * The one case where a one-shot does move the pad is when the pad is still in the loop mode
 * this function chose for the sound that just left it. That mode was the app's decision, not
 * the user's, so it does not outlive the loop it was made for — otherwise dropping a kick
 * onto a pad that used to hold a break gives you a kick that repeats until you stop it.
 *
 * Every default here is a starting point: playback mode and quantise are both in the pad
 * menu, and nothing re-applies these on top of a choice the user has made.
 */
export function assignAssetToPad(padID: UUID, assetID: UUID, label = 'Assign Sound'): void {
  let assigned: { asset: SampleAsset; isLoop: boolean; padName: string } | null = null;
  mutate(label, (target) => {
    const asset = projectAsset(target, assetID);
    const pad = projectPad(target, padID);
    if (asset === undefined || pad === undefined) return;
    pad.assetID = assetID;
    pad.edit = structuredClone(asset.defaultEdit);
    if (pad.name === '') pad.name = asset.name;
    clampSampleEdit(pad.edit, asset.duration);

    const isLoop = projectAssetIsLoop(target, asset);
    if (isLoop) {
      pad.playbackMode = 'loop';
      pad.quantize = 'bar';
    } else if (pad.playbackMode === 'loop') {
      pad.playbackMode = 'oneShot';
      if (pad.quantize === 'bar') pad.quantize = 'off';
    }
    assigned = { asset, isLoop, padName: pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name };
  });
  if (assigned !== null) offerLoopTempoFit(assigned);
}

/**
 * Tells the user, once, that the loop they just placed does not run at the project's tempo —
 * and offers the one tap that fixes it.
 *
 * Never silent, in either direction: a loop with a known tempo gets the fit offer, and a loop
 * with no tempo at all gets the offer to go and find one. Assigning a 90 BPM break into a 120
 * BPM project and hearing it drift with no explanation is the failure this exists to prevent.
 */
function offerLoopTempoFit(assigned: { asset: SampleAsset; isLoop: boolean; padName: string }): void {
  const open = projectSignal.peek();
  if (open === null || !assigned.isLoop) return;
  const { asset } = assigned;
  const tempo = open.tempo;

  if (asset.detectedBPM === undefined || asset.detectedBPM <= 0) {
    pushBanner({
      kind: 'info',
      title: `${asset.name} is a loop`,
      message: `It is set to loop on ${assigned.padName}, quantised to the bar. PadLab does not know its tempo yet.`,
      actionLabel: 'Find the tempo',
      action: () => void detectAssetTempo(asset.id),
      timeout: 9000,
    });
    return;
  }

  const detected = Math.round(asset.detectedBPM);
  if (!loopNeedsTempoFit(asset, tempo, 1)) {
    pushBanner({
      kind: 'success',
      title: `${asset.name} fits this project`,
      message: `${detected} BPM, looping on ${assigned.padName} and quantised to the bar.`,
      timeout: 4000,
    });
    return;
  }

  const rate = loopFitRate(asset, tempo);
  pushBanner({
    kind: 'warning',
    title: `${asset.name} runs at ${detected} BPM`,
    message: `This project is at ${Math.round(tempo)}. Fitting plays it ${rate > 1 ? 'faster' : 'slower'} (${rate.toFixed(3)}×) so it sits on the grid.`,
    actionLabel: 'Fit to tempo',
    action: () => fitAssetToProjectTempo(asset.id),
    timeout: 0,
  });
}

/**
 * Stretches a sound to the project's tempo, on the asset and on every pad already playing it.
 *
 * Pads follow deliberately: fitting only the asset's `defaultEdit` would leave the pad the
 * user is looking at playing at the old rate, and "fit" would appear to have done nothing.
 */
export function fitAssetToProjectTempo(assetID: UUID): void {
  const open = projectSignal.peek();
  if (open === null) return;
  const asset = projectAsset(open, assetID);
  if (asset === undefined) return;
  const rate = loopFitRate(asset, open.tempo);
  mutate('Fit Loop To Tempo', (target) => {
    const live = projectAsset(target, assetID);
    if (live === undefined) return;
    live.defaultEdit.playbackRate = rate;
    for (const pad of projectAllPads(target)) {
      if (pad.assetID !== assetID) continue;
      pad.edit.playbackRate = rate;
    }
  });
  notify(`Fitted to ${Math.round(open.tempo)} BPM`, `“${asset.name}” now plays at ${rate.toFixed(3)}×.`);
}

/**
 * Runs the tempo estimator over a sound and stores the answer.
 *
 * Shared by the library row's action and the "find the tempo" offer a loop with no BPM gets
 * when it lands on a pad, so both report the same thing in the same words.
 */
export async function detectAssetTempo(assetID: UUID): Promise<number | null> {
  const open = projectSignal.peek();
  if (open === null) return null;
  const asset = projectAsset(open, assetID);
  if (asset === undefined) return null;
  const buffer = await bufferForAsset(asset);
  if (buffer === null) {
    reportFailure('Tempo detection failed', new Error('That sound could not be loaded.'));
    return null;
  }
  // The analysis worker can fail to start; a missing tempo is a message, never a crash.
  const result = await analysis.tempo(buffer).catch(() => ({ bpm: null as number | null, confidence: 0 }));
  if (result.bpm === null) {
    notify('No steady tempo found', 'This clip does not have a clear pulse. You can still slice it by hand.');
    return null;
  }
  const bpm = result.bpm;
  mutate('Detect Tempo', (target) => {
    const live = projectAsset(target, assetID);
    if (live !== undefined) live.detectedBPM = bpm;
  });
  notify(`${Math.round(bpm)} BPM`, `${Math.round(result.confidence * 100)}% confident.`);
  return bpm;
}

/** Saves a rendered buffer as a new asset — the exit route from every lab in the app. */
export async function saveRenderedAsAsset(
  audio: RenderedAudio | AudioBuffer,
  name: string,
  origin: SampleAsset['origin'],
  tags: string[] = [],
): Promise<SampleAsset> {
  const blob = encodeWav(audio, 24);
  const result = await importAudioBlob(blob, name, origin, { tags });
  return result.asset;
}

export { toRenderedAudio, renderEdit, encodeWav };

// MARK: - Pad state and triggering

const queuedUntil = new Map<UUID, number>();

/** True while a quantised trigger is waiting for its grid line — the pad's queued state. */
export function isPadQueued(padID: UUID): boolean {
  const until = queuedUntil.get(padID);
  if (until === undefined) return false;
  if (engine.now() >= until) {
    queuedUntil.delete(padID);
    return false;
  }
  return true;
}

export function padStateFor(pad: Pad, open: Project): PadState {
  if (pad.isMuted) return 'muted';
  if (engine.isLooping(pad.id)) return 'looping';
  if (engine.isSounding(pad.id)) return 'playing';
  if (isPadQueued(pad.id)) return 'queued';
  // A take being captured for this pad — "Record into this pad" arms exactly one — is the
  // pad's own state, not a banner somewhere else on the screen.
  if (recorder.isCapturing.value && selectedPadID.value === pad.id) return 'recording';
  if (pad.isSoloed) return 'soloed';
  if (padIsEmpty(pad)) return 'empty';
  const asset = projectAsset(open, pad.assetID);
  if (asset === undefined) return 'missingSource';
  const issue = padIssues.get(pad.id);
  if (issue !== undefined) return issue;
  if (!engine.isPrepared(pad.id)) return 'processing';
  return 'loaded';
}

export function triggerPad(pad: Pad, options: TriggerOptions = {}): void {
  let when = options.when;
  if (when === undefined) {
    // Silence is never the safe default: with no running clock there is nothing to quantise
    // against, so the pad plays now rather than waiting for a bar that never arrives.
    const quantised = engine.transport.nextQuantizedTime(pad.quantize);
    if (quantised !== null) {
      when = quantised;
      queuedUntil.set(pad.id, quantised);
    }
  }
  // Audio first, always: nothing between the touch and this call may allocate or await.
  const played = engine.trigger(pad, when === undefined ? options : { ...options, when });
  if (played) {
    haptic(6);
    sequencer.capturePerformanceHit(pad.id, options.velocity ?? 0.9);
  }
}

export function releasePad(pad: Pad): void {
  if (playbackModeStopsOnRelease(pad.playbackMode)) {
    engine.release(pad.id, pad.playbackMode === 'gate' ? pad.releaseFade : 0.01);
  }
}

export function togglePad(pad: Pad, options: TriggerOptions = {}): void {
  if (engine.isSounding(pad.id)) {
    engine.stopPad(pad.id);
    // Toggling off is a stop, not a pause — unless the pad is explicitly set to continue
    // from where it left off, which is the one mode that wants the playhead kept.
    if (pad.retrigger !== 'resume') engine.rewind(pad.id);
  } else triggerPad(pad, options);
}

/**
 * Silences one pad because its editor is opening.
 *
 * A pad that is not in `hold` or `gate` mode keeps sounding through everything that is not an
 * explicit stop: `releasePad` ends only those two modes, so a loop, a toggle or a long
 * one-shot survived the walk into the wave editor, the Sound Lab and the Voice Lab. The
 * editor then auditioned a sample over the top of the same sample still running underneath,
 * and a Voice Lab take was recorded with the loop leaking into the microphone.
 *
 * Deliberately narrow. It stops this pad and nothing else: not the transport, which is the
 * user's own running state and stays on screen in the Sound Lab, and not the other pads,
 * which are a performance nobody asked to end. `panic()` is still the one control that stops
 * everything.
 *
 * `engine.stopPad` fades over 8 ms rather than cutting, so the silence has no click, and the
 * playhead it records is left in place: `preparePad` already drops that offset whenever an
 * edit changes the audio, so a `resume` pad opened and closed unchanged still continues where
 * it was. The queued marker goes too, or a quantised hit that has not landed yet would fire
 * into the editor the user just opened.
 */
export function stopPadForEditing(padID: UUID): void {
  engine.stopPad(padID);
  queuedUntil.delete(padID);
}

/**
 * Silences the whole board when a pad menu opens.
 *
 * Stopping only the pad being edited was not enough in practice: the menu covers most of the
 * screen, so anything else still sounding is a loop the user can no longer reach the tile for,
 * and every decision in that menu is one about a sound they cannot hear properly. The transport
 * is deliberately left running, because it is the user's own state and stopping it would lose
 * their place; a running sequencer will start the pattern again on its next step, which is the
 * correct behaviour for a performance and not something to fight.
 */
export function stopAllForEditing(): void {
  engine.stopAllPads();
  queuedUntil.clear();
}

export function panic(): void {
  engine.panic();
  queuedUntil.clear();
  haptic([12, 40, 12]);
}

// MARK: - Sequencer
//
// The look-ahead subscriber. It converts the pattern into scheduled voices inside the
// window the transport hands it and does nothing else — no view state, no allocation beyond
// the step objects the model already owns.

export type PerformanceRecordMode = 'off' | 'overdub' | 'replace';

class Sequencer {
  readonly recordMode = signal<PerformanceRecordMode>('off');
  readonly isSongMode = signal(false);
  readonly automationArmed = signal(false);
  readonly sectionIndex = signal(0);

  /**
   * The pattern queued to take over at the end of the current pass, and the scene that
   * queued it.
   *
   * Pattern chaining and scene launching are the same mechanism seen from two angles: both
   * mean "not now, at the bar line". Tapping a pattern chip used to assign `currentPatternID`
   * directly, and `pattern()` re-read that signal on every schedule window, so a pattern
   * changed *inside* the current bar — audibly wrong, and impossible to perform with.
   */
  readonly queuedPatternID = signal<UUID | null>(null);
  readonly queuedSceneID = signal<UUID | null>(null);
  /** The pattern actually being scheduled, which only ever changes on a bar line. */
  private activePatternID: UUID | null = null;
  /** Pads a launched scene is holding, so the next scene can release them. */
  private sceneHeld: UUID[] = [];

  private detach: (() => void) | null = null;
  private scheduledThrough = -1;
  private sectionRepeat = 0;
  /**
   * Recorded parameter moves, keyed by automation *target* rather than by lane id.
   *
   * The producer (the Sound Lab's X/Y pad) knows which pad it is driving, not which lane —
   * and a pattern that has never been automated has no lanes at all. Keying by target means
   * `commitAutomation` can create the lane it needs, which is exactly what native does in
   * `SequencerService.swift:380`.
   */
  private readonly automationWrites = new Map<string, { target: AutomationTarget; points: { beat: number; value: number }[] }>();
  /** One probability stream per track, mirroring the renderer's. Reset on every `play()`. */
  private readonly trackRandoms = new Map<string, SeededRandom>();

  attach(): void {
    if (this.detach !== null) return;
    this.detach = engine.transport.onSchedule((window) => this.schedule(window));
  }

  pattern(): Pattern | null {
    const open = projectSignal.peek();
    if (open === null) return null;
    if (this.isSongMode.peek() && open.song.sections.length > 0) {
      const section = open.song.sections[Math.min(this.sectionIndex.peek(), open.song.sections.length - 1)];
      const found = open.patterns.find((entry) => entry.id === section?.patternID);
      if (found !== undefined) return found;
    }
    // While the transport runs the *active* pattern is authoritative, so a queued change
    // cannot take effect between two steps of the bar it was requested in.
    const id = (engine.transport.isPlaying ? this.activePatternID : null) ?? currentPatternID.peek();
    return open.patterns.find((entry) => entry.id === id) ?? open.patterns[0] ?? null;
  }

  /**
   * Asks for `patternID` to play from the end of the current pass. Stopped, it simply
   * becomes the current pattern — there is no bar line to wait for.
   */
  queuePattern(patternID: UUID): void {
    if (!engine.transport.isPlaying) {
      this.queuedPatternID.value = null;
      this.activePatternID = patternID;
      currentPatternID.value = patternID;
      return;
    }
    this.queuedPatternID.value = patternID;
  }

  cancelQueuedPattern(): void {
    this.queuedPatternID.value = null;
    this.queuedSceneID.value = null;
  }

  /**
   * Launches a scene: its pattern takes over at the next bar line and its held pads start
   * with it. Stopped, it starts the transport on that pattern immediately.
   */
  launchScene(scene: Scene): void {
    this.queuedSceneID.value = scene.id;
    if (!engine.transport.isPlaying) {
      if (scene.patternID !== undefined) this.queuePattern(scene.patternID);
      void this.play().then(() => this.applyScene(scene, engine.now()));
      return;
    }
    if (scene.patternID !== undefined) this.queuedPatternID.value = scene.patternID;
  }

  /** Releases the pads the previous scene held and starts this one's. */
  private applyScene(scene: Scene, at: number): void {
    const open = projectSignal.peek();
    for (const padID of this.sceneHeld) {
      if (scene.heldPadIDs.includes(padID)) continue;
      engine.stopPad(padID, at);
    }
    if (open !== null) {
      for (const padID of scene.heldPadIDs) {
        if (this.sceneHeld.includes(padID)) continue;
        const pad = projectPad(open, padID);
        if (pad !== undefined) engine.trigger(pad, { when: at, isSequenced: true });
      }
    }
    this.sceneHeld = [...scene.heldPadIDs];
    this.queuedSceneID.value = null;
  }

  /**
   * Hands over to whatever is queued. Called only at a pattern boundary, which is what makes
   * "queued" mean anything.
   */
  private applyQueued(open: Project, at: number): void {
    const sceneID = this.queuedSceneID.peek();
    const patternID = this.queuedPatternID.peek();
    if (patternID !== null) {
      this.queuedPatternID.value = null;
      if (open.patterns.some((entry) => entry.id === patternID)) {
        this.activePatternID = patternID;
        currentPatternID.value = patternID;
      }
    }
    if (sceneID !== null) {
      const scene = open.scenes.find((entry) => entry.id === sceneID);
      if (scene !== undefined) this.applyScene(scene, at);
      else this.queuedSceneID.value = null;
    }
  }

  async play(): Promise<void> {
    const open = projectSignal.peek();
    const pattern = this.pattern();
    if (open === null || pattern === null) return;
    await engine.unlock();

    // The transport's position *is* `AudioContext.currentTime` measured against an anchor, so
    // starting it on a context that did not resume produces the worst possible failure: the
    // UI says "playing", the playhead never moves, and nothing makes a sound — with no way to
    // tell that from a bug in the sequencer itself. If the context is not running, say so and
    // leave the transport stopped; the shell's unlock bar is already visible in that state.
    if (!engine.isUnlocked.peek()) {
      pushBanner({
        kind: 'warning',
        title: 'Audio is not running yet',
        message: 'Tap "Tap to turn on sound" at the top of the screen, then press play again.',
        timeout: 6000,
      });
      return;
    }

    this.attach();
    this.scheduledThrough = -1;
    this.sectionRepeat = 0;
    this.trackRandoms.clear();
    this.replaceCleared.clear();
    this.activePatternID = pattern.id;
    this.queuedPatternID.value = null;
    const transport = engine.transport;
    transport.setTempo(open.tempo);
    transport.setSwing(open.swing);
    transport.setStepsPerBeat(pattern.stepsPerBeat);
    transport.setCountIn(settingsSignal.peek().countInEnabled ? open.metronome.countInBars : 0, open.timeSignatureBeats);
    transport.setPatternWindow(0, pattern.length / Math.max(1, pattern.stepsPerBeat));
    transport.play();
  }

  stop(): void {
    engine.transport.stop();
    this.commitAutomation();
    this.scheduledThrough = -1;
    this.trackRandoms.clear();
    this.replaceCleared.clear();
    this.queuedPatternID.value = null;
    this.queuedSceneID.value = null;
    for (const padID of this.sceneHeld) engine.stopPad(padID);
    this.sceneHeld = [];
    this.activePatternID = null;
  }

  /** The next draw from a track's probability stream, created on first use. */
  private stepRoll(pattern: Pattern, trackID: UUID): number {
    const key = `${pattern.id}/${trackID}`;
    let random = this.trackRandoms.get(key);
    if (random === undefined) {
      random = trackRandom(pattern.id, trackID);
      this.trackRandoms.set(key, random);
    }
    return random.unit();
  }

  toggle(): void {
    if (engine.transport.isPlaying) this.stop();
    else void this.play();
  }

  private schedule(window: ScheduleWindow): void {
    const open = projectSignal.peek();
    const pattern = this.pattern();
    if (open === null || pattern === null) return;
    const transport = engine.transport;
    const stepSeconds = transport.secondsPerStep;
    if (stepSeconds <= 0) return;

    const firstStep = Math.max(0, Math.ceil(transport.beatsAtTime(window.now) * pattern.stepsPerBeat));
    const lastStep = Math.floor(transport.beatsAtTime(window.horizon) * pattern.stepsPerBeat);
    const start = Math.max(firstStep, this.scheduledThrough + 1);

    for (let position = start; position <= lastStep; position++) {
      const beat = position / Math.max(1, pattern.stepsPerBeat);
      const base = transport.timeForBeat(beat);
      if (base === null) continue;
      const swing = pattern.swing ?? open.swing;
      const time = base + (position % 2 === 1 ? swing * stepSeconds * 0.5 : 0);
      this.scheduleStep(open, pattern, position, time, stepSeconds);
      this.scheduledThrough = position;
    }
  }

  private scheduleStep(open: Project, pattern: Pattern, position: number, time: number, stepSeconds: number): void {
    const perBeat = Math.max(1, pattern.stepsPerBeat);
    if (open.metronome.isEnabled && position % perBeat === 0) {
      const beat = position / perBeat;
      engine.metronome.schedule(time, beat % open.timeSignatureBeats === 0, open.metronome.volume);
    }

    const anySoloed = pattern.tracks.some((track) => track.isSoloed);
    for (const track of pattern.tracks) {
      if (track.isMuted || (anySoloed && !track.isSoloed)) continue;
      // The pad is resolved before the roll, and the roll happens for every active step even
      // at probability 1 — both to match the renderer's draw order exactly
      // (`offline-render.ts:3009`, `:3040`). The stream is the same seeded one the bounce
      // uses, so a probability pattern sounds the same tomorrow as it does now and matches
      // the export it was auditioned against; `Math.random()` made every pass different and
      // none of them the export.
      const pad = track.padID === undefined ? undefined : projectPad(open, track.padID);
      if (pad === undefined) continue;
      const step = trackStepAtGlobalPosition(track, position);
      if (step === undefined || !step.isActive) continue;
      // A loop track fires once per pass, from its earliest lit step, and every other lit
      // step on it is inert. Without this a loop placed on step 9 starts a second copy of the
      // same bar half a bar out of phase with the first — two beds fighting, and no way for
      // the user to tell why the pattern sounds smeared. The grid draws the same leading step
      // this reads, so what is lit and what sounds are the same cell.
      if (track.oneHitPerPass === true) {
        const span = Math.max(1, track.length);
        if (((position % span) + span) % span !== trackLeadingStepIndex(track)) continue;
      }
      const roll = this.stepRoll(pattern, track.id);
      if (step.probability < 0.999 && roll >= step.probability) continue;
      const hitTime = time + step.microTiming * stepSeconds;
      // Scheduled for the hit, not for the moment the step was queued: this runs up to a
      // 150 ms lookahead window early, and stopping now would leave a hole in front of it.
      if (step.chokesPrevious) engine.stopPad(pad.id, hitTime);
      // Ratchets subdivide a step. A bed that spans the pattern has no step to subdivide, so
      // eight retriggers of it would be eight restarts of the same bar inside 167 ms.
      const ratchets = track.oneHitPerPass === true ? 1 : Math.max(1, Math.round(step.ratchets));
      for (let index = 0; index < ratchets; index++) {
        engine.trigger(pad, {
          velocity: step.velocity,
          pitchOffset: step.pitchOffset,
          when: hitTime + (index * stepSeconds) / ratchets,
          isRatchet: index > 0,
          isSequenced: true,
        });
      }
    }

    for (const lane of pattern.automation) {
      if (!lane.isEnabled) continue;
      const value = automationValueAt(lane.points, position / perBeat);
      if (value !== null) applyAutomation(open, lane.target, value);
    }

    if ((position + 1) % pattern.length === 0) this.advanceSong(open, pattern, time + stepSeconds);
  }

  private advanceSong(open: Project, pattern: Pattern, boundary: number): void {
    // The one moment a queued pattern or scene is allowed to take over.
    this.applyQueued(open, boundary);
    const next = this.pattern() ?? pattern;
    const beats = next.length / Math.max(1, next.stepsPerBeat);
    engine.transport.setStepsPerBeat(next.stepsPerBeat);
    engine.transport.setPatternWindow(engine.transport.liveBeats, beats);
    if (!this.isSongMode.peek() || open.song.sections.length === 0) return;
    const section = open.song.sections[this.sectionIndex.peek()];
    if (section === undefined) return;
    this.sectionRepeat++;
    if (this.sectionRepeat >= section.repeatCount) {
      this.sectionRepeat = 0;
      this.sectionIndex.value = (this.sectionIndex.peek() + 1) % open.song.sections.length;
    }
  }

  /**
   * Tracks wiped by the current replace pass, keyed `<trackID>@<pass>`.
   *
   * Replace means "this pass overwrites what was there", not "every hit overwrites every
   * other hit". The clear ran unconditionally on each hit, so the second note of a take
   * erased the first and a replace recording could never hold more than one step per track.
   */
  private readonly replaceCleared = new Set<string>();

  /**
   * Quantises a live pad hit onto the grid and writes it into the armed track.
   *
   * `velocity` is how hard the pad was actually struck. It used to be discarded and written
   * as a flat 0.9, so a performance recorded through the pads came back without any of its
   * dynamics — the one thing playing them rather than drawing them was for.
   */
  capturePerformanceHit(padID: UUID, velocity = 0.9): void {
    const mode = this.recordMode.peek();
    const transport = engine.transport;
    if (mode === 'off' || !transport.isPlaying) return;
    const pattern = this.pattern();
    if (pattern === null) return;
    const raw = Math.max(0, transport.liveBeats * pattern.stepsPerBeat);
    const nearest = Math.round(raw);
    const drift = clamp(raw - nearest, -0.5, 0.5);
    const hit = clamp(velocity, 0.05, 1);
    mutate(
      'Record Performance',
      (target) => {
        const live = target.patterns.find((entry) => entry.id === pattern.id);
        if (live === undefined) return;
        let track = live.tracks.find((entry) => entry.padID === padID);
        if (track === undefined) {
          const pad = projectPad(target, padID);
          track = makeTrack({
            padID,
            name: pad?.name ?? 'Track',
            length: live.length,
            color: pad?.color ?? 'magenta',
            // A track the performance recorder invents for a loop pad is a loop track from
            // the first hit. Recording four bars of a break being punched in would otherwise
            // write four overlapping copies of the same bar.
            oneHitPerPass: pad !== undefined && projectPadPlaysLoop(target, pad),
          });
          live.tracks.push(track);
        }
        // Fold onto the track's own length, not the pattern's. A polyrhythmic track is
        // shorter than the pattern and `trackStepAtGlobalPosition` reads it modulo its own
        // length, so a hit indexed by the pattern landed past the end of what playback ever
        // looks at — recorded, saved, and permanently silent.
        const span = Math.max(1, track.length);
        const index = ((nearest % span) + span) % span;
        while (track.steps.length < span) track.steps.push({ ...OFF_STEP });
        if (mode === 'replace') {
          const pass = Math.floor(Math.max(0, raw) / span);
          const key = `${track.id}@${pass}`;
          if (!this.replaceCleared.has(key)) {
            this.replaceCleared.add(key);
            for (let cursor = 0; cursor < span; cursor++) track.steps[cursor] = { ...OFF_STEP };
          }
        }
        // One hit per pass means one lit step, so the newest hit *moves* the bed rather than
        // adding a second copy of it. Leaving the old step lit would keep a step the
        // scheduler ignores on the grid, which is exactly the "why is nothing happening
        // there?" this mode exists to stop.
        if (track.oneHitPerPass === true) {
          for (let cursor = 0; cursor < span; cursor++) track.steps[cursor] = { ...OFF_STEP };
        }
        track.steps[index] = { ...OFF_STEP, isActive: true, velocity: hit, microTiming: drift };
      },
      { coalesceKey: `perform-${padID}-${nearest}` },
    );
  }

  /** Records a live parameter move into the pattern's automation while the transport runs. */
  writeAutomation(target: AutomationTarget, value: number): void {
    if (!this.automationArmed.peek() || !engine.transport.isPlaying) return;
    const key = automationTargetKey(target);
    let entry = this.automationWrites.get(key);
    if (entry === undefined) {
      entry = { target, points: [] };
      this.automationWrites.set(key, entry);
    }
    entry.points.push({ beat: Math.max(0, engine.transport.liveBeats), value });
  }

  /**
   * Writes the recorded moves into the pattern that was playing, creating a lane per target
   * where none existed. Committing into *every* pattern would scatter one performance across
   * the whole project, so the sweep lands only where it was played.
   */
  private commitAutomation(): void {
    if (this.automationWrites.size === 0) return;
    const writes = [...this.automationWrites.values()];
    this.automationWrites.clear();
    const patternID = this.pattern()?.id;
    if (patternID === undefined) return;
    mutate('Record Automation', (target) => {
      const pattern = target.patterns.find((entry) => entry.id === patternID);
      if (pattern === undefined) return;
      for (const write of writes) {
        const key = automationTargetKey(write.target);
        let lane = pattern.automation.find((entry) => automationTargetKey(entry.target) === key);
        if (lane === undefined) {
          lane = makeAutomationLane(write.target);
          pattern.automation.push(lane);
        }
        lane.points = simplifyPoints([...write.points].sort((a, b) => a.beat - b.beat));
        lane.isEnabled = true;
      }
    });
  }
}

/**
 * A stable identity for an automation target, so a second sweep of the same control extends
 * the lane it already has instead of stacking a duplicate on top of it.
 */
function automationTargetKey(target: AutomationTarget): string {
  if ('padVolume' in target) return `padVolume/${target.padVolume.padID}`;
  if ('padPan' in target) return `padPan/${target.padPan.padID}`;
  if ('masterVolume' in target) return 'masterVolume';
  return `effectParameter/${target.effectParameter.nodeID}/${target.effectParameter.key}`;
}

const OFF_STEP: Step = {
  isActive: false,
  velocity: 0.85,
  probability: 1,
  pitchOffset: 0,
  microTiming: 0,
  ratchets: 1,
  chokesPrevious: false,
};

export function offStepValue(): Step {
  return { ...OFF_STEP };
}

function automationValueAt(points: readonly { beat: number; value: number }[], beat: number): number | null {
  const first = points[0];
  if (first === undefined) return null;
  if (beat <= first.beat) return first.value;
  const last = points[points.length - 1]!;
  if (beat >= last.beat) return last.value;
  for (let index = 1; index < points.length; index++) {
    const previous = points[index - 1]!;
    const next = points[index]!;
    if (beat <= next.beat) {
      const span = next.beat - previous.beat;
      return span <= 0 ? next.value : previous.value + (next.value - previous.value) * ((beat - previous.beat) / span);
    }
  }
  return last.value;
}

function simplifyPoints(points: { beat: number; value: number }[]): { beat: number; value: number }[] {
  if (points.length <= 2) return points;
  const kept = [points[0]!];
  for (let index = 1; index < points.length - 1; index++) {
    const previous = kept[kept.length - 1]!;
    const current = points[index]!;
    const next = points[index + 1]!;
    const span = next.beat - previous.beat;
    const expected = span > 0 ? previous.value + ((next.value - previous.value) * (current.beat - previous.beat)) / span : current.value;
    if (Math.abs(current.value - expected) > 0.004) kept.push(current);
  }
  kept.push(points[points.length - 1]!);
  return kept;
}

/**
 * Automation is applied straight to the nodes, never through `mutate` — a recorded sweep
 * would otherwise fill the undo stack with thousands of steps during playback.
 */
function applyAutomation(open: Project, target: AutomationTarget, value: number): void {
  const anySoloed = projectAllPads(open).some((entry) => entry.isSoloed);
  if ('padVolume' in target) {
    const pad = projectPad(open, target.padVolume.padID);
    if (pad !== undefined) engine.applyPad({ ...pad, volume: clamp(value, 0, 1) }, anySoloed, open.tempo);
    return;
  }
  if ('padPan' in target) {
    const pad = projectPad(open, target.padPan.padID);
    if (pad !== undefined) engine.applyPad({ ...pad, pan: clamp(value, -1, 1) }, anySoloed, open.tempo);
    return;
  }
  if ('masterVolume' in target) {
    engine.applyMaster(
      clamp(value, 0, 1),
      open.master.headroomDB,
      open.master.limiterEnabled,
      open.master.limiterCeilingDB,
      open.master.chain,
      open.tempo,
    );
    return;
  }
  // The fourth case. It was silently absent, so an `effectParameter` lane — which the model
  // defines, the schema serialises and the offline renderer honours — recorded, saved and
  // reloaded without ever moving anything during playback.
  engine.setEffectParameter(target.effectParameter.nodeID, target.effectParameter.key, value);
}

export const sequencer = new Sequencer();

// MARK: - Synthesis
//
// Starter content is generated on the device rather than downloaded: it is licence-clean by
// construction, costs no bytes in the bundle, and means a freshly installed PadLab is
// playable with no network at all.

export type PercussionKind = 'kick' | 'snare' | 'hat' | 'clap' | 'tom' | 'rim' | 'laser' | 'riser' | 'impact' | 'blip';

export const PERCUSSION_KINDS: readonly PercussionKind[] = [
  'kick',
  'snare',
  'hat',
  'clap',
  'tom',
  'rim',
  'laser',
  'riser',
  'impact',
  'blip',
];

export function percussionDisplayName(kind: PercussionKind): string {
  const names: Record<PercussionKind, string> = {
    kick: 'Kick',
    snare: 'Snare',
    hat: 'Hi-Hat',
    clap: 'Clap',
    tom: 'Tom',
    rim: 'Rim',
    laser: 'Laser',
    riser: 'Riser',
    impact: 'Impact',
    blip: 'Blip',
  };
  return names[kind];
}

const PERCUSSION_SECONDS: Record<PercussionKind, number> = {
  kick: 0.55,
  snare: 0.32,
  hat: 0.1,
  clap: 0.36,
  tom: 0.45,
  rim: 0.12,
  laser: 0.5,
  riser: 1.6,
  impact: 1.4,
  blip: 0.18,
};

/**
 * How the Sound Lab's two synth knobs reach the generator.
 *
 * They were signals nothing read: two labelled knobs on the synth panel with no effect on
 * the rendered audio. Both are expressed as scalars over the recipe below rather than as new
 * per-kind parameters, so every voice responds and none of them needs a second recipe.
 */
export interface PercussionShape {
  /** 0 dark, 0.5 as designed, 1 bright. Scales every oscillator and filter frequency. */
  tone?: number;
  /** 0 short, 0.5 as designed, 1 long. Scales every envelope and the rendered length. */
  decay?: number;
}

export async function synthesisePercussion(
  kind: PercussionKind,
  sampleRate = 48000,
  shape: PercussionShape = {},
): Promise<AudioBuffer> {
  const toneScale = 0.5 + clamp(shape.tone ?? 0.5, 0, 1) * 1.1;
  const decayScale = 0.45 + clamp(shape.decay ?? 0.5, 0, 1) * 1.55;
  const seconds = PERCUSSION_SECONDS[kind] * decayScale;
  const ctx = new OfflineAudioContext(1, Math.ceil(seconds * sampleRate), sampleRate);
  const out = ctx.createGain();
  out.gain.value = 0.9;
  out.connect(ctx.destination);

  const noiseSource = (): AudioBufferSourceNode => {
    const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(sampleRate * seconds)), sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index++) data[index] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    return source;
  };
  // Every recipe below is written at the design point; `toneScale` and `decayScale` are
  // applied here so the two knobs reach all ten voices through one path.
  const tone = (type: OscillatorType, from: number, to: number, decay: number, level: number): void => {
    const length = decay * decayScale;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from * toneScale, 0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to * toneScale), length);
    const env = ctx.createGain();
    env.gain.setValueAtTime(level, 0);
    env.gain.exponentialRampToValueAtTime(0.0001, length);
    osc.connect(env).connect(out);
    osc.start(0);
    osc.stop(length + 0.01);
  };
  const noise = (decay: number, level: number, filter: BiquadFilterType, frequency: number, at = 0): void => {
    const length = decay * decayScale;
    const start = at * decayScale;
    const source = noiseSource();
    const band = ctx.createBiquadFilter();
    band.type = filter;
    band.frequency.value = Math.min(sampleRate * 0.45, frequency * toneScale);
    band.Q.value = 0.9;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, start);
    env.gain.linearRampToValueAtTime(level, start + 0.002);
    env.gain.exponentialRampToValueAtTime(0.0001, start + length);
    source.connect(band).connect(env).connect(out);
    source.start(start);
  };

  switch (kind) {
    case 'kick':
      tone('sine', 140, 42, 0.42, 1);
      noise(0.02, 0.35, 'lowpass', 900);
      break;
    case 'snare':
      tone('triangle', 220, 160, 0.14, 0.5);
      noise(0.22, 0.75, 'highpass', 1400);
      break;
    case 'hat':
      noise(0.07, 0.55, 'highpass', 7000);
      break;
    case 'clap':
      for (let index = 0; index < 3; index++) noise(0.09, 0.6, 'bandpass', 1600, index * 0.012);
      noise(0.28, 0.35, 'bandpass', 1200, 0.04);
      break;
    case 'tom':
      tone('sine', 260, 90, 0.38, 0.9);
      break;
    case 'rim':
      tone('square', 1700, 900, 0.05, 0.35);
      noise(0.04, 0.3, 'bandpass', 2400);
      break;
    case 'laser':
      tone('sawtooth', 2400, 120, 0.45, 0.55);
      break;
    case 'riser': {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(120 * toneScale, 0);
      osc.frequency.exponentialRampToValueAtTime(2600 * toneScale, seconds);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(400 * toneScale, 0);
      filter.frequency.exponentialRampToValueAtTime(Math.min(sampleRate * 0.45, 9000 * toneScale), seconds);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, 0);
      env.gain.exponentialRampToValueAtTime(0.8, seconds * 0.92);
      env.gain.exponentialRampToValueAtTime(0.0001, seconds);
      osc.connect(filter).connect(env).connect(out);
      osc.start(0);
      osc.stop(seconds);
      break;
    }
    case 'impact':
      tone('sine', 90, 30, 1.1, 1);
      noise(0.9, 0.5, 'lowpass', 600);
      break;
    case 'blip':
      tone('square', 880, 880, 0.14, 0.4);
      break;
  }
  return ctx.startRendering();
}

// MARK: - Templates

export interface StarterTemplate {
  id: string;
  name: string;
  kicker: string;
  summary: string;
  symbol: string;
  grid: import('../model/types').GridSize;
  tempo: number;
  kit: PercussionKind[];
  seedPattern: boolean;
}

export const STARTER_TEMPLATES: readonly StarterTemplate[] = [
  {
    id: 'blank',
    name: 'Blank Board',
    kicker: 'Start empty',
    summary: 'Twelve empty pads. Record or import your own sounds.',
    symbol: 'square.grid.3x3.fill',
    grid: 'g3x4',
    tempo: 96,
    kit: [],
    seedPattern: false,
  },
  {
    id: 'beat-kit',
    name: 'Beat Kit',
    kicker: 'Make a beat',
    summary: 'A generated drum kit on a 4 × 4 grid, with a starter pattern already written.',
    symbol: 'square.grid.3x3.fill',
    grid: 'g4x4',
    tempo: 92,
    kit: ['kick', 'snare', 'hat', 'clap', 'tom', 'rim', 'impact', 'blip'],
    seedPattern: true,
  },
  {
    id: 'voice-fx',
    name: 'Voice Board',
    kicker: 'Play with your voice',
    summary: 'Six large pads sized for recording takes and dropping them straight in.',
    symbol: 'mouth.fill',
    grid: 'g2x3',
    tempo: 96,
    kit: [],
    seedPattern: false,
  },
  {
    id: 'live-set',
    name: 'Live Set',
    kicker: 'Perform',
    summary: 'Twenty-four pads across two banks, quantised triggering, limiter armed.',
    symbol: 'waveform.path',
    grid: 'g4x6',
    tempo: 124,
    kit: ['kick', 'snare', 'hat', 'clap', 'riser', 'laser'],
    seedPattern: true,
  },
  {
    id: 'podcast',
    name: 'Podcast Board',
    kicker: 'Cue clips live',
    summary: 'Twelve one-shot pads with a shared choke group, so two stings never overlap.',
    symbol: 'mouth.fill',
    grid: 'g3x4',
    tempo: 100,
    kit: ['impact', 'blip', 'riser'],
    seedPattern: false,
  },
];

const TEMPLATE_PALETTE: import('../model/types').PadColor[] = [
  'magenta',
  'violet',
  'cyan',
  'acid',
  'mint',
  'coral',
  'amber',
  'indigo',
];

export async function createProjectFromTemplate(template: StarterTemplate, name?: string): Promise<Project> {
  const banks = [makeBank('Bank A', { grid: template.grid })];
  if (template.id === 'live-set') banks.push(makeBank('Bank B', { grid: template.grid }));
  const created = makeProject(name ?? template.name, {
    banks,
    tempo: template.tempo,
    patterns: [makePattern('Pattern 1', { length: 16, stepsPerBeat: 4 })],
  });
  if (template.id === 'podcast') for (const pad of created.banks[0]!.pads) pad.chokeGroup = 1;
  if (template.id === 'live-set') for (const bank of created.banks) for (const pad of bank.pads) pad.quantize = 'bar';

  for (let index = 0; index < template.kit.length; index++) {
    const kind = template.kit[index]!;
    try {
      const buffer = await synthesisePercussion(kind);
      const asset = await stageAsset(created, encodeWav(buffer, 16), percussionDisplayName(kind), ['starter', 'drums']);
      const pad = created.banks[0]!.pads[index];
      if (pad !== undefined) {
        pad.assetID = asset.id;
        pad.name = percussionDisplayName(kind);
        pad.color = TEMPLATE_PALETTE[index % TEMPLATE_PALETTE.length]!;
        if (kind === 'hat') pad.chokeGroup = 9;
      }
      engine.putBuffer(asset.id, buffer);
    } catch (error) {
      log.error('Starter sound generation failed', error, { kind: tag(kind) });
    }
  }

  if (template.seedPattern) {
    const pattern = created.patterns[0]!;
    const rows = [
      { padIndex: 0, steps: [0, 4, 8, 11] },
      { padIndex: 1, steps: [4, 12] },
      { padIndex: 2, steps: [0, 2, 4, 6, 8, 10, 12, 14] },
      { padIndex: 3, steps: [12] },
    ];
    for (const row of rows) {
      const pad = created.banks[0]!.pads[row.padIndex];
      if (pad === undefined || padIsEmpty(pad)) continue;
      const track = makeTrack({ padID: pad.id, name: pad.name, length: 16, color: pad.color });
      for (const step of row.steps) {
        const target = track.steps[step];
        if (target !== undefined) {
          target.isActive = true;
          target.velocity = step === 0 ? 1 : 0.85;
        }
      }
      pattern.tracks.push(track);
    }
  }
  return sanitiseProject(created);
}

/** Import used while building a project that is not open yet, so it cannot use `mutate`. */
async function stageAsset(target: Project, blob: Blob, displayName: string, tags: string[]): Promise<SampleAsset> {
  const id = newUUID();
  await blobStore().put(assetKey(target.id, id), blob, { mimeType: 'audio/wav' });
  const bytes = await blob.arrayBuffer();
  const buffer = await engine.decode(bytes.slice(0), displayName);
  const asset = makeSampleAsset({
    id,
    name: displayName,
    fileName: `${id}.wav`,
    originalFileName: `${displayName}.wav`,
    origin: 'synthesised',
    duration: buffer.duration,
    sampleRate: buffer.sampleRate,
    channelCount: buffer.numberOfChannels,
    frameCount: buffer.length,
    byteSize: blob.size,
    contentHash: await analysis.hash(blob).catch(() => ''),
    licence: userOwnedLicence(),
    tags,
  });
  target.assets.push(asset);
  return asset;
}

// MARK: - Sound packs (the target of a /pack/<id> deep link)

export interface LocalSoundPack {
  id: string;
  name: string;
  summary: string;
  assetIDs: UUID[];
}

/** Packs the open project can offer as a shareable link. Both are real and both are local. */
export const localPacks = derive((open) => {
  const packs: LocalSoundPack[] = [];
  const starter = open.assets.filter((asset) => asset.tags.includes('starter'));
  if (starter.length > 0) {
    packs.push({
      id: `starter-${open.id}`,
      name: `${open.name} starter kit`,
      summary: 'Generated on this device, licence-clean, safe to share.',
      assetIDs: starter.map((asset) => asset.id),
    });
  }
  const favourites = open.assets.filter((asset) => asset.isFavourite);
  if (favourites.length > 0) {
    packs.push({
      id: `favourites-${open.id}`,
      name: `${open.name} favourites`,
      summary: 'Every sound you starred in this project.',
      assetIDs: favourites.map((asset) => asset.id),
    });
  }
  return packs;
}, [] as LocalSoundPack[]);

// MARK: - Boot

export function engineLatencySummary(): { median: number; p95: number; worst: number; samples: number } {
  const values = [...engine.triggerLatencies()].sort((a, b) => a - b);
  if (values.length === 0) return { median: 0, p95: 0, worst: 0, samples: 0 };
  const at = (fraction: number): number => values[Math.min(values.length - 1, Math.floor(values.length * fraction))] ?? 0;
  return { median: at(0.5), p95: at(0.95), worst: values[values.length - 1] ?? 0, samples: values.length };
}

/** Everything launch needs, in the order it needs it. Never touches the microphone. */
export async function bootstrap(): Promise<{ lastProjectID: UUID | null }> {
  loadSettings();
  storeSignal.value = await getProjectStore();
  blobsSignal.value = await getBlobStore();
  void refreshStorageReport();
  const emptied = await projectStore().emptyExpiredTrash().catch(() => 0);
  if (emptied > 0) log.info('Expired trash removed', { count: emptied });

  // Keeps the sounding graph in step with every edit, including undo and redo.
  effect(() => {
    revisionSignal.value;
    if (projectSignal.value !== null) queueSync();
  });

  return { lastProjectID: await projectStore().lastOpenedProjectID() };
}
